// NbaOptimizer.jsx — FULL DROP-IN (v5.1 — NFL-style layout)
// Tweaks:
// • Export CSV button moved to Lineup Results header.
// • Lineup Results table: first ~20 rows visible, scroll past that.
// • Boosting projections no longer reorders player rows.
// • Added Reset toolbar: All Rules, Boosts, Locks, Excludes, Min/Max, Constraints, Filters.

import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../utils/api";

/* ------------------------------ helpers ------------------------------ */
const clamp = (v, lo = 0, hi = 1e9) =>
  Math.max(lo, Math.min(hi, Number.isFinite(+v) ? +v : lo));
const pickFirst = (row, keys) => {
  for (const k of keys) if (row && row[k] != null) return row[k];
  return undefined;
};
const upper = (s) => {
  const v = String(s || "").toUpperCase().trim();
  // Normalize common site-specific NBA abbreviations so FD/DK/projection rows line up.
  const aliases = {
    SA: "SAS",
    GS: "GSW",
    NY: "NYK",
    NO: "NOP",
    PHO: "PHX",
    UTH: "UTA",
  };
  return aliases[v] || v;
};
const stripAccents = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const normalizeName = (name) => {
  let n = stripAccents(String(name || "").toLowerCase());
  n = n.replace(/[.,'’"-]/g, " ").replace(/\s+/g, " ").trim();
  n = n.replace(/\b[a-z]\b/g, "").replace(/\s{2,}/g, " ").trim();
  n = n.replace(/\b(jr|jr\.|sr|sr\.|ii|iii|iv|v)\b/g, "").replace(/\s{2,}/g, " ").trim();
  return n;
};
const splitPos = (s) =>
  String(s || "").toUpperCase().replace(/\s+/g, "").split("/").filter(Boolean);
const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
function toHhMm(timeStr) {
  if (!timeStr) return "";
  const raw = String(timeStr).trim();
  if (/(am|pm)/i.test(raw)) {
    try {
      return new Date(`1970-01-01T${raw}`).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {}
    return raw.replace(/\s*(am|pm)\s*$/i, (m) => ` ${m.toUpperCase()}`);
  }
  let hh = 0,
    mm = 0;
  if (/^\d{4}$/.test(raw)) {
    hh = +raw.slice(0, 2);
    mm = +raw.slice(2, 4);
  } else if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [H, M] = raw.split(":");
    hh = +H;
    mm = +M;
  } else return raw;
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = ((hh + 11) % 12) + 1;
  return `${hh}:${String(mm).padStart(2, "0")} ${ampm}`;
}

function nbaTimeToMinutes(timeStr) {
  const s = String(timeStr || "").trim();
  if (!s) return -1;

  const m = s.match(/(\d{1,2}):(\d{2})\s*([AP])M/i);
  if (!m) return -1;

  let hh = Number(m[1]) % 12;
  const mm = Number(m[2]) || 0;
  if (/p/i.test(m[3])) hh += 12;

  return hh * 60 + mm;
}

const geoMeanPct = (items, accessor) => {
  const vals = (items || [])
    .map((x) => Number(accessor(x)))
    .filter((v) => Number.isFinite(v) && v > 0);

  if (!vals.length) return 0;

  const logAvg = vals.reduce((sum, v) => sum + Math.log(v), 0) / vals.length;
  return Math.exp(logAvg);
};

// CSV helpers
function toCsv(rows) {
  return rows
    .map((r) =>
      r
        .map((v) => {
          const s = String(v ?? "");
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(",")
    )
    .join("\n");
}
function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ------------------------------ data hooks ------------------------------ */
function useJson(url) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        const j = ct.includes("json") ? await r.json() : JSON.parse(await r.text());
        if (alive) setData(j);
      } catch {
        if (alive) setData(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { data };
}

function useLastUpdated(dataUrl) {
  const [d, setD] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      // 1) Prefer meta.json (export time), matching the artifact for the given dataUrl
      try {
        const metaUrl = META_SRC; // or: dataUrl.replace(/[^/]+$/, "meta.json");
        const r = await fetch(metaUrl + `?_=${Date.now()}`, { cache: "no-store" });
        if (r.ok) {
          const j = await r.json();
          const arts = Array.isArray(j?.artifacts) ? j.artifacts : [];

          // try to match by exact web path first, then by filename
          const want = String(dataUrl || "").split("/").pop();
          const art =
            arts.find(a => a?.path === dataUrl) ||
            arts.find(a => String(a?.latest || "").replace(/\\/g, "/").endsWith(dataUrl)) ||
            arts.find(a => String(a?.path || "").split("/").pop() === want) ||
            arts.find(a => String(a?.latest || "").split("/").pop() === want) ||
            null;

          const iso =
            art?.last_updated ||
            art?.tags?.last_updated ||
            j?.last_updated ||
            j?.generated_at ||
            null;

          if (alive && iso) {
            setD(new Date(iso));
            return; // we got a real "last updated"
          }
        }
      } catch {
        /* fall through to HTTP headers */
      }

      // 2) Fallback to file Last-Modified/Date (server clock)
      try {
        const h = await fetch(dataUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified") || h.headers.get("date");
        if (alive && lm) {
          setD(new Date(lm));
          return;
        }
      } catch {}

      // 3) Final fallback: GET and read headers
      try {
        const r = await fetch(dataUrl + `?_=${Date.now()}`, { cache: "no-store" });
        const lm = r.headers.get("last-modified") || r.headers.get("date");
        if (alive && lm) {
          setD(new Date(lm));
          return;
        }
      } catch {}

      if (alive) setD(null);
    })();

    return () => {
      alive = false;
    };
  }, [dataUrl]);

  return d;
}

const useStickyState = (key, init) => {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? JSON.parse(raw) : init;
    } catch {
      return init;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {}
  }, [key, v]);

  return [v, setV];
};

const useStickySet = (key, initSet = new Set()) => {
  const [raw, setRaw] = useStickyState(key, Array.from(initSet));
  const valueSet = useMemo(() => new Set(raw), [raw]);

  const setValue = (next) => {
    if (typeof next === "function") {
      setRaw((prev) => {
        const prevSet = new Set(prev);
        const out = next(prevSet);
        return Array.from(out instanceof Set ? out : new Set(out || []));
      });
    } else {
      setRaw(Array.from(next instanceof Set ? next : new Set(next || [])));
    }
  };

  return [valueSet, setValue];
};

/* ------------------------------ sources ------------------------------ */
const PROJ_SRC = "/data/nba/latest/projections.json";
const DATA_SRC = "/data/nba/latest/data.json";
const SITE_IDS = "/data/nba/latest/site_ids/site_ids.json";
const NAME_XWALK = "/data/nba/latest/name_xwalk.json";
const META_SRC = "/data/nba/latest/meta.json";

/* ------------------------------ sites ------------------------------ */
const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    cap: 50000,
    posFields: ["DK Pos", "Pos", "DK Position"],
    projFields: ["DK Proj", "Projection", "Points"],
    pownFields: ["DK pOWN%", "DK pOWN"],
    salFieldsProj: ["DK Salary"],
    salFieldsData: ["DK Sal"],
    slots: [
      { name: "PG", eligible: ["PG"] },
      { name: "SG", eligible: ["SG"] },
      { name: "SF", eligible: ["SF"] },
      { name: "PF", eligible: ["PF"] },
      { name: "C", eligible: ["C"] },
      { name: "G", eligible: ["PG", "SG"] },
      { name: "F", eligible: ["SF", "PF"] },
      { name: "UTIL", eligible: ["PG", "SG", "SF", "PF", "C"] },
    ],
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    cap: 60000,
    posFields: ["FD Pos", "Pos", "FD Position"],
    projFields: ["FD Proj", "Projection", "Points"],
    pownFields: ["FD pOWN%", "FD pOWN"],
    salFieldsProj: ["FD Salary"],
    salFieldsData: ["FD Sal"],
    slots: [
      { name: "PG", eligible: ["PG"] },
      { name: "SG", eligible: ["SG"] },
      { name: "SF", eligible: ["SF"] },
      { name: "PF", eligible: ["PF"] },
      { name: "C", eligible: ["C"] },
      { name: "G/F", eligible: ["PG", "SG", "SF", "PF"] },
      { name: "F/C", eligible: ["SF", "PF", "C"] },
      { name: "UTIL1", eligible: ["PG", "SG", "SF", "PF", "C"] },
      { name: "UTIL2", eligible: ["PG", "SG", "SF", "PF", "C"] },
    ],
  },
};

const ORDER_DK = ["PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"];
const ORDER_FD = ["PG", "SG", "SF", "PF", "C", "G/F", "F/C", "UTIL1", "UTIL2"];

/* ------------------------------ streaming solver + fallbacks ------------------------------ */
function extractPlayers(evt) {
  if (!evt) return [];
  if (Array.isArray(evt.players)) return evt.players;
  if (Array.isArray(evt.drivers)) return evt.drivers;
  if (evt.lineup && Array.isArray(evt.lineup.players)) return evt.lineup.players;
  if (Array.isArray(evt.items)) {
    const f = evt.items.find((x) => Array.isArray(x.players));
    if (f) return f.players;
  }
  return [];
}
function extractAssignment(evt) {
  if (!evt) return null;
  if (Array.isArray(evt.assignment)) return evt.assignment;
  if (evt.lineup && Array.isArray(evt.lineup.assignment)) return evt.lineup.assignment;
  if (Array.isArray(evt.slots)) return evt.slots;
  return null;
}

// tolerant stream reader: SSE (data: ...), NDJSON, or plain chunked JSON objects/arrays
async function solveStreamNBA(payload, onItem, onDone) {
  const res = await fetch(`${API_BASE}/solve_nba_stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok || !res.body) throw new Error("Stream failed");
  const rd = res.body.getReader();
  const dec = new TextDecoder("utf-8");
  let buf = "";

  // brace counter for generic JSON objects
  let depth = 0,
    chunkStart = 0;

  while (true) {
    const { done, value } = await rd.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });

    // 1) SSE lines
    const sseParts = buf.split(/\r?\n\r?\n/);
    if (sseParts.length > 1) {
      buf = sseParts.pop() ?? "";
      for (const part of sseParts) {
        const line = part.split(/\r?\n/).find((l) => l.startsWith("data:"));
        if (!line) continue;
        try {
          const evt = JSON.parse(line.replace(/^data:\s*/, ""));
          if (evt.done) onDone?.(evt);
          else
            onItem({
              players: extractPlayers(evt),
              assignment: extractAssignment(evt),
              total: evt.total ?? evt.sum ?? evt.proj ?? 0,
              salary: evt.salary ?? evt.sal ?? 0,
            });
        } catch {}
      }
      continue;
    }

    // 2) NDJSON
    const ndjsonParts = buf.split(/\r?\n/);
    if (ndjsonParts.length > 1) {
      buf = ndjsonParts.pop() ?? "";
      for (const line of ndjsonParts) {
        const t = line.trim();
        if (!t) continue;
        try {
          const evt = JSON.parse(t);
          if (evt.done) onDone?.(evt);
          else
            onItem({
              players: extractPlayers(evt),
              assignment: extractAssignment(evt),
              total: evt.total ?? evt.sum ?? evt.proj ?? 0,
              salary: evt.salary ?? evt.sal ?? 0,
            });
        } catch {
          /* fall through to brace parsing */
        }
      }
    }

    // 3) best-effort brace scanning for standalone objects
    for (let i = 0; i < buf.length; i++) {
      const ch = buf[i];
      if (ch === "{") {
        if (depth === 0) chunkStart = i;
        depth++;
      } else if (ch === "}") {
        depth--;
        if (depth === 0) {
          const slice = buf.slice(chunkStart, i + 1);
          try {
            const evt = JSON.parse(slice);
            onItem({
              players: extractPlayers(evt),
              assignment: extractAssignment(evt),
              total: evt.total ?? evt.sum ?? evt.proj ?? 0,
              salary: evt.salary ?? evt.sal ?? 0,
            });
            // consume processed part
            buf = buf.slice(i + 1);
            i = -1; // restart scanning
          } catch {}
        }
      }
    }
  }
}

async function solveFallbackNBA(payload, onItem) {
  const res = await fetch(`${API_BASE}/solve_nba`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const arr = await res.json();
  for (const raw of (Array.isArray(arr) ? arr : [])) {
    const lineup = {
      players: extractPlayers(raw) || raw.players || raw.drivers || [],
      assignment: extractAssignment(raw),
      total: raw.total ?? raw.sum ?? raw.proj ?? 0,
      salary: raw.salary ?? raw.sal ?? 0,
    };
    onItem(lineup);
  }
}

/* ------------------------------ small components ------------------------------ */
const hexToRgba = (hex, a = 1) => {
  const h = String(hex || "").replace("#", "").trim();
  if (h.length !== 6) return `rgba(148,163,184,${a})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const TeamBadge = ({ team, isDark }) => {
  const colors = {
    ATL: "#E03A3E",
    BOS: "#007A33",
    BKN: "#000000",
    CHA: "#1D1160",
    CHI: "#CE1141",
    CLE: "#6F263D",
    DAL: "#00538C",
    DEN: "#0E2240",
    DET: "#C8102E",
    GSW: "#1D428A",
    HOU: "#CE1141",
    IND: "#002D62",
    LAC: "#C8102E",
    LAL: "#552583",
    MEM: "#5D76A9",
    MIA: "#98002E",
    MIL: "#00471B",
    MIN: "#0C2340",
    NOP: "#0C2340",
    NYK: "#F58426",
    OKC: "#007AC1",
    ORL: "#0077C0",
    PHI: "#006BB6",
    PHX: "#1D1160",
    POR: "#E03A3E",
    SAC: "#5A2D81",
    SAS: "#C4CED4",
    TOR: "#CE1141",
    UTA: "#002B5C",
    WAS: "#002B5C",
  };

  const bg = (colors[team] || "#64748b").toLowerCase();

  // --- same idea as the NFL optimizer: pick readable text based on luminance ---
  const hexToRGB = (hex) => {
    const h = String(hex || "#888").replace("#", "");
    if (h.length !== 6) return { r: 136, g: 136, b: 136 };
    const v = parseInt(h, 16);
    return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
  };
  const readableText = (hex) => {
    const { r, g, b } = hexToRGB(hex);
    // simple perceived luminance (matches NFL file style)
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return L < 140 ? "#FFFFFF" : "#111111";
  };

  const fg = readableText(bg);

  return (
    <span
      className="inline-flex items-center justify-center rounded-md px-2 py-[2px] text-[11px] font-semibold"
      style={{
        backgroundColor: bg,
        color: fg,
        minWidth: 34,
        letterSpacing: "0.02em",
        // A tiny outline so black/dark colors still pop in dark mode
        boxShadow: isDark
          ? "0 0 0 1px rgba(15,23,42,0.45), 0 0 0 2px rgba(255,255,255,0.06)"
          : "0 0 0 1px rgba(15,23,42,0.10)",
      }}
      title={team}
    >
      {team}
    </span>
  );
};
const teamLogo = (abbr) => `/logos/nba/${abbr}.png`;

/* ====================================================================== */

export default function NbaOptimizer({ activeTab = "optimizer", setActiveTab = () => {} }) {
  const { data: proj } = useJson(PROJ_SRC);
  const { data: dataRows } = useJson(DATA_SRC);
  const { data: siteIds } = useJson(SITE_IDS);
  const { data: nameXwalk } = useJson(NAME_XWALK);
  const updatedAt = useLastUpdated(PROJ_SRC);

  const [site, setSite] = useStickyState("nba.site", "dk");
  const [theme, setTheme] = useStickyState("nba.theme", "dark");
  const isDark = theme !== "light";
  const CFG = SITES[site];
  const panelClass = isDark
    ? "border border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
    : "border border-slate-200 bg-slate-50 shadow-sm";
  const inputClass = "h-8 px-2 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-sky-400 " +
    (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500" : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400");
  const mutedClass = isDark ? "text-slate-400" : "text-slate-600";
  const tabClass = (active) =>
    "relative h-9 whitespace-nowrap pb-2 text-sm font-semibold inline-flex items-center gap-2 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 " +
    (active ? (isDark ? "text-white" : "text-slate-900") : (isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"));

  const SALARY_RENDER_MAX = 20000; // hide/exclude players above this salary (used for view + chips + optimizer)

  const [numLineups, setNumLineups] = useStickyState("nba.n", 20);
  const [maxSalary, setMaxSalary] = useStickyState("nba.maxSalary", CFG.cap);
  useEffect(() => {
    setMaxSalary(SITES[site].cap);
  }, [site]);
  const [globalMax, setGlobalMax] = useStickyState("nba.gmax", 100);
  const [randomness, setRandomness] = useStickyState("nba.rand", 0);
  const [maxLineupPown, setMaxLineupPown] = useStickyState("nba.maxLP", "");
  const [maxFromTeam, setMaxFromTeam] = useStickyState("nba.maxTeam", "");
  const [maxGeoMeanPown, setMaxGeoMeanPown] = useStickyState("nba.maxGeoMeanPown", "");

  const key = (k) => `nba.${site}.${k}`;
  const [locks, setLocks] = useStickySet(key("locks"));
  const [excls, setExcls] = useStickySet(key("excls"));
  const [minPct, setMinPct] = useStickyState(key("min"), {});
  const [maxPct, setMaxPct] = useStickyState(key("max"), {});
  const [boost, setBoost] = useStickyState(key("boost"), {});
  const [teamBoost, setTeamBoost] = useStickyState(key("teamBoost"), {});
  const bumpTeamBoost = (team, step) =>
    setTeamBoost((m) => ({ ...m, [team]: clamp((Number(m[team]) || 0) + step, -6, 6) }));
  const [groups, setGroups] = useStickyState(key("groups"), []);
  const [customProj, setCustomProj] = useStickyState(key("customProj"), {});
  const [customProjDraft, setCustomProjDraft] = useState({});
  const [hideUnprojected, setHideUnprojected] = useStickyState(key("hideUnprojected"), true);
  const [hideExcluded, setHideExcluded] = useStickyState(key("hideExcluded"), false);
  const [allowedUnprojected, setAllowedUnprojected] = useStickySet(key("allowedUnprojected"));

    // Saved builds per site (DK / FD)
  // Each build: { id, label, createdAt, lineups, lineupIds }
  const [builds, setBuilds] = useStickyState(key("builds"), []);
  const [activeBuildId, setActiveBuildId] = useStickyState(key("activeBuildId"), null);

  const [posFilter, setPosFilter] = useState("ALL");
  const [q, setQ] = useState("");
  const [gameFilter, setGameFilter] = useState(() => new Set()); // include games
  const [teamExcl, setTeamExcl] = useState(() => new Set()); // exclude TEAMS only
  const [teamInclude, setTeamInclude] = useState(() => new Set()); // include-only teams
  const [gameExcl, setGameExcl] = useState(() => new Set()); // exclude whole game key
  const [slate, setSlate] = useStickyState("nba.slate", "main");
  const xwalk = useMemo(() => {
    const m = new Map();
    if (nameXwalk && typeof nameXwalk === "object") {
      for (const [k, v] of Object.entries(nameXwalk))
        m.set(normalizeName(k), normalizeName(v));
    }
    return m;
  }, [nameXwalk]);
  const aliasKey = (name) => {
    const base = normalizeName(name);
    return xwalk.get(base) || base;
  };

const dataByName = useMemo(() => {
  const arr = Array.isArray(dataRows) ? dataRows : [];
  const m = new Map();

  for (const r of arr) {
    const raw = r.Player || r.PLAYER || r.player || r.name;
    if (!raw) continue;

    const k = aliasKey(raw);

    m.set(k, {
      time: toHhMm(r.Time || r.time || ""),
      dkSal: r["DK Sal"] ?? r["DK Salary"],
      fdSal: r["FD Sal"] ?? r["FD Salary"],
      team: r.Team || r.TEAM || r.team_abbr,
      opp: r.Opp || r.OPP || r.opponent,
      pos: r.Pos || r.Position || r["DK Pos"] || r["FD Pos"] || r.pos,

      // NEW
      ou: Number(r["O/U"]) || 0,
      iTot: Number(r["I. Tot"]) || 0,
      line: Number(r["Line"]) || 0,
    });
  }

  return m;
}, [dataRows, xwalk]);

  const siteIdsByName = useMemo(() => {
    const m = new Map();

    const addRec = (nameLike, recLike = {}) => {
      const rec = recLike && typeof recLike === "object" ? recLike : {};
      const name = String(
        rec.name ??
          rec.Name ??
          rec.player ??
          rec.Player ??
          rec.displayName ??
          rec.DisplayName ??
          nameLike ??
          ""
      ).trim();

      if (!name) return;

      // Preserve the object-key name because some NBA site_ids files store
      // players as { "Player Name": { id, salary, pos } } with no name field.
      const merged = { ...rec, name: rec.name ?? rec.Name ?? rec.player ?? rec.Player ?? name };
      const k = aliasKey(name);

      if (!m.has(k)) m.set(k, merged);

      // Add a few extra aliases for display names / raw names.
      for (const alias of [rec.name, rec.Name, rec.player, rec.Player, rec.displayName, rec.DisplayName]) {
        const a = String(alias || "").trim();
        if (a && !m.has(aliasKey(a))) m.set(aliasKey(a), merged);
      }
    };

    const addBucket = (bucket) => {
      if (!bucket) return;

      if (Array.isArray(bucket)) {
        for (const rec of bucket) addRec(null, rec);
        return;
      }

      if (typeof bucket === "object") {
        for (const [nm, rec] of Object.entries(bucket)) addRec(nm, rec);
      }
    };

    // Support every site_ids shape we use across tools:
    // { site_ids: { dk: {name: rec}, fd: ... } }
    // { site_ids: { dk: [rec], fd: [rec] } }
    // { dk: [rec] / {name: rec}, fd: ... }
    // { sites: { dk: [rec] / {name: rec}, fd: ... } }
    addBucket(siteIds?.site_ids?.[site]);
    addBucket(siteIds?.sites?.[site]);
    addBucket(siteIds?.[site]);

    return m;
  }, [siteIds, site, xwalk]);

  const allSiteIdsByName = useMemo(() => {
    const m = new Map();

    const addRec = (nameLike, recLike = {}, sourceSite = "") => {
      const rec = recLike && typeof recLike === "object" ? recLike : {};
      const name = String(
        rec.name ??
          rec.Name ??
          rec.player ??
          rec.Player ??
          rec.displayName ??
          rec.DisplayName ??
          nameLike ??
          ""
      ).trim();
      if (!name) return;

      const merged = {
        ...rec,
        sourceSite,
        name: rec.name ?? rec.Name ?? rec.player ?? rec.Player ?? name,
      };
      const k = aliasKey(name);
      if (!m.has(k)) m.set(k, merged);

      for (const alias of [rec.name, rec.Name, rec.player, rec.Player, rec.displayName, rec.DisplayName]) {
        const a = String(alias || "").trim();
        if (a && !m.has(aliasKey(a))) m.set(aliasKey(a), merged);
      }
    };

    const addBucket = (bucket, sourceSite) => {
      if (!bucket) return;
      if (Array.isArray(bucket)) {
        for (const rec of bucket) addRec(null, rec, sourceSite);
        return;
      }
      if (typeof bucket === "object") {
        for (const [nm, rec] of Object.entries(bucket)) addRec(nm, rec, sourceSite);
      }
    };

    for (const sKey of ["dk", "fd"]) {
      addBucket(siteIds?.site_ids?.[sKey], sKey);
      addBucket(siteIds?.sites?.[sKey], sKey);
      addBucket(siteIds?.[sKey], sKey);
    }

    return m;
  }, [siteIds, xwalk]);

  const getSiteId = (name) => {
    const rec = siteIdsByName.get(aliasKey(name)) || {};
    const useLate = slate === "late";

    // Strict slate IDs:
    // - Late slate requires late_id (no fallback to main id)
    // - Main slate requires id (no fallback to late_id)
    const primaryId = useLate
      ? (rec.late_id ?? rec.LateID ?? rec.LATE_ID ?? "")
      : (rec.id ?? rec.ID ?? rec.main_id ?? rec.MAIN_ID ?? "");

    const id =
      primaryId ??
      (useLate ? "" : (rec.player_id ?? rec.dkid ?? rec.fdid ?? rec.fid ?? rec.PlayerID ?? ""));

    const slateCode = rec.slate_id ?? rec.slate ?? rec.SLATE_ID ?? "";
    return { id: id || "", slate: slateCode };
  };

  // base rows (no boosts/custom projections yet)
  const baseRows = useMemo(() => {
    const base = Array.isArray(proj?.rows)
      ? proj.rows
      : Array.isArray(proj?.players)
      ? proj.players
      : Array.isArray(proj?.data)
      ? proj.data
      : Array.isArray(proj)
      ? proj
      : [];

    const projectedRows = base
      .map((r, idx) => {
        const nameRaw = r.Player ?? r.player ?? r.Name ?? r.name ?? "";
        const k = aliasKey(nameRaw);
        if (!nameRaw) return null;

        const posRaw = pickFirst(r, CFG.posFields) ?? "";
        const posList = splitPos(posRaw);
        if (!posList.length) return null;
        const posDisp = posList.join("/");

        const projVal = Number(pickFirst(r, CFG.projFields)) || 0;

        const getPownFraction = (row) => {
          let usedKey = null;
          for (const key of CFG.pownFields) {
            if (row && row[key] != null) {
              usedKey = key;
              break;
            }
          }
          const rawVal = usedKey ? row[usedKey] : undefined;
          const hasPercentSignOrKey =
            (usedKey && usedKey.includes("%")) ||
            /%/.test(String(rawVal ?? ""));

          const n = Number(String(rawVal ?? "").replace(/[ %]/g, ""));
          if (!Number.isFinite(n)) return 0;

          if (hasPercentSignOrKey) return n / 100;
          return n <= 1 ? n : n / 100;
        };

        const pownPct = getPownFraction(r);
        const fromData = dataByName.get(k) || {};
        const team = upper(r.Team ?? r.team ?? r.Tm ?? fromData.team);
        const opp = upper(r.Opp ?? r.opp ?? fromData.opp);
        const rec = siteIdsByName.get(k) || {};

        const mainId = rec.id ?? null;
        const mainSalaryId = rec.salary ?? null;
        const lateId = rec.late_id ?? null;
        const lateSalaryId = rec.late_salary ?? null;

        const hasMain = mainId != null && mainSalaryId != null;
        const hasLate = lateId != null && lateSalaryId != null;

        let slateTag = "main";
        if (hasLate && !hasMain) slateTag = "late";
        else if (hasLate && hasMain) slateTag = "both";

        const salProj = pickFirst(r, CFG.salFieldsProj);
        const salData = site === "dk" ? fromData.dkSal : fromData.fdSal;
        const salaryFromIds = slate === "late" ? lateSalaryId : mainSalaryId;

        let salary = 0;
        if (Number.isFinite(+salaryFromIds)) salary = +salaryFromIds;
        else if (Number.isFinite(+salProj)) salary = +salProj;
        else if (Number.isFinite(+salData)) salary = +salData;

        const time = fromData.time || "";
        const gameKey = team && opp ? [team, opp].sort().join("@") : "";
        const baseVal = salary > 0 ? (projVal / salary) * 1000 : 0;

        return {
          name: nameRaw,
          key: k,
          team,
          opp,
          salary,
          time,
          proj: projVal,
          baseProj: projVal,
          pown: pownPct,
          baseVal,
          val: baseVal,
          posList,
          posDisp,
          gameKey,
          slate: slateTag,
          hasProjection: true,
          siteIdOnly: false,

          ou: Number(fromData.ou) || 0,
          iTot: Number(fromData.iTot) || 0,
          line: Number(fromData.line) || 0,

          _idx: idx,
        };
      })
      .filter((r) => r && r.name && r.posList.length);

    const seenKeys = new Set(projectedRows.map((r) => r.key));
    const seenIds = new Set(
      projectedRows
        .flatMap((r) => {
          const rec = siteIdsByName.get(r.key) || {};
          return [rec.id, rec.late_id, rec.player_id, rec.dkid, rec.fdid, rec.PlayerID];
        })
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const siteIdOnlyRows = [];
    let extraIdx = projectedRows.length;

    for (const [k, recRaw] of siteIdsByName.entries()) {
      if (!recRaw || typeof recRaw !== "object") continue;
      if (seenKeys.has(k)) continue;

      const nameRaw =
        recRaw.name ??
        recRaw.Name ??
        recRaw.player ??
        recRaw.Player ??
        recRaw.displayName ??
        recRaw.DisplayName ??
        "";
      if (!nameRaw) continue;

      const mainId =
        recRaw.id ??
        recRaw.ID ??
        recRaw.main_id ??
        recRaw.MAIN_ID ??
        recRaw.player_id ??
        recRaw.dkid ??
        recRaw.fdid ??
        recRaw.PlayerID ??
        "";
      const lateId = recRaw.late_id ?? recRaw.LateID ?? recRaw.LATE_ID ?? "";
      const activeId = slate === "late" ? lateId : mainId;
      if (!activeId || seenIds.has(String(activeId).trim())) continue;

      const mainSalary = recRaw.salary ?? recRaw.Salary ?? recRaw.main_salary ?? recRaw.MAIN_SALARY;
      const lateSalary = recRaw.late_salary ?? recRaw.lateSalary ?? recRaw.LATE_SALARY;
      const salary = Number(slate === "late" ? lateSalary : mainSalary) || 0;
      if (!salary || salary > SALARY_RENDER_MAX) continue;

      const fromData = dataByName.get(k) || dataByName.get(aliasKey(nameRaw)) || {};

      // FD NBA site_ids often omit position, while DK includes it for the same player.
      // Pull position from any same-name site_id record first, then fall back to data rows.
      const crossRec = allSiteIdsByName.get(k) || allSiteIdsByName.get(aliasKey(nameRaw)) || {};
      const posRaw =
        recRaw.pos ??
        recRaw.Pos ??
        recRaw.position ??
        recRaw.Position ??
        recRaw.rosterPosition ??
        crossRec.pos ??
        crossRec.Pos ??
        crossRec.position ??
        crossRec.Position ??
        crossRec.rosterPosition ??
        fromData.pos ??
        "";
      const posList = splitPos(posRaw);
      if (!posList.length) continue;
      const posDisp = posList.join("/");

      const team = upper(recRaw.Team ?? recRaw.team ?? recRaw.Tm ?? fromData.team);
      const opp = upper(recRaw.Opp ?? recRaw.opp ?? recRaw.OPP ?? fromData.opp);
      const time = toHhMm(recRaw.Time ?? recRaw.time ?? fromData.time ?? "");
      const gameKey = team && opp ? [team, opp].sort().join("@") : "";

      const hasMain = !!mainId && Number(mainSalary) > 0;
      const hasLate = !!lateId && Number(lateSalary) > 0;
      let slateTag = "main";
      if (hasLate && !hasMain) slateTag = "late";
      else if (hasLate && hasMain) slateTag = "both";

      siteIdOnlyRows.push({
        name: String(nameRaw),
        key: k,
        team,
        opp,
        salary,
        time,
        proj: 0,
        baseProj: 0,
        pown: 0,
        baseVal: 0,
        val: 0,
        posList,
        posDisp,
        gameKey,
        slate: slateTag,
        hasProjection: false,
        siteIdOnly: true,

        ou: Number(fromData.ou) || 0,
        iTot: Number(fromData.iTot) || 0,
        line: Number(fromData.line) || 0,

        _idx: extraIdx++,
      });
    }

    return [...projectedRows, ...siteIdOnlyRows];
  }, [proj, site, CFG, dataByName, siteIdsByName, allSiteIdsByName, slate]);
  // apply custom projections + player boosts (±3%) + team boosts (±10% per step) — NOTE: rows order should not change when boosts change.
  const rows = useMemo(() => {
    return baseRows.map((r) => {
      const rawCustom = customProj?.[r.name];
      const hasCustomProj = rawCustom != null && String(rawCustom).trim() !== "";
      const activeProj = hasCustomProj ? Math.max(0, Number(rawCustom) || 0) : (r.baseProj ?? r.proj ?? 0);

      const b = typeof boost[r.name] === "number" ? boost[r.name] : 0;
      const teamSteps = Number(teamBoost[r.team]) || 0;
      const mult = 1 + b * 0.03 + teamSteps * 0.10;
      const eproj = activeProj * mult;
      const evalv = r.salary > 0 ? (eproj / r.salary) * 1000 : 0;
      return {
        ...r,
        proj: activeProj,
        customProj: rawCustom ?? "",
        hasCustomProj,
        eproj,
        val: evalv,
      };
    });
    // IMPORTANT: no dependency on 'boost' for ordering; ordering handled later.
  }, [baseRows, boost, teamBoost, customProj]);

  const defaultExcludedNames = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      if (r.siteIdOnly && !allowedUnprojected.has(r.name)) s.add(r.name);
    }
    return s;
  }, [rows, allowedUnprojected]);

  const effectiveExcls = useMemo(() => {
    const s = new Set(excls);
    for (const n of defaultExcludedNames) s.add(n);
    return s;
  }, [excls, defaultExcludedNames]);

  /* games + teams for chips */
const slateRowsForChips = useMemo(() => {
  const slateOK = (r) => {
    if (slate === "late") return r.slate === "late" || r.slate === "both";
    return r.slate === "main" || r.slate === "both";
  };

  return rows.filter((r) => {
    if (!r) return false;
    if (!slateOK(r)) return false;
    if (r.salary && r.salary > SALARY_RENDER_MAX) return false;
    const sid = getSiteId(r.name).id;
    return !!sid;
  });
}, [rows, slate, site]);

  const games = useMemo(() => {
    const m = new Map();
    for (const r of slateRowsForChips) {
      if (!r.gameKey) continue;
      const [a, b] = r.gameKey.split("@");
      if (!m.has(r.gameKey)) m.set(r.gameKey, { a, b, time: r.time });
    }
    return [...m.values()].sort((x, y) => {
      const timeX = nbaTimeToMinutes(x.time);
      const timeY = nbaTimeToMinutes(y.time);

      // Put games without a valid time last.
      return (timeX < 0 ? Infinity : timeX) -
             (timeY < 0 ? Infinity : timeY);
    });
  }, [slateRowsForChips]);

  const allTeams = useMemo(() => {
    const s = new Set();
    slateRowsForChips.forEach((r) => {
      if (r.team) s.add(r.team);
      if (r.opp) s.add(r.opp);
    });
    return [...s].sort();
  }, [slateRowsForChips]);

  const gameMetaByKey = useMemo(() => {
  const m = new Map();

  for (const r of slateRowsForChips) {
    if (!r.gameKey) continue;

    if (!m.has(r.gameKey)) {
      m.set(r.gameKey, {
        ou: 0,
        teams: {},
      });
    }

    const g = m.get(r.gameKey);

    if (!g.ou && Number(r.ou) > 0) g.ou = Number(r.ou);

    if (r.team && Number(r.iTot) > 0 && g.teams[r.team] == null) {
      g.teams[r.team] = Number(r.iTot);
    }
  }

  return m;
}, [slateRowsForChips]);

  // When switching DK/FD or Main/Late, prune stale chip selections
  // so you don't keep games/teams that aren't on the newly selected slate.
  useEffect(() => {
    const gameKeys = new Set(
      games.map((g) => [g.a, g.b].sort().join("@"))
    );
    const teams = new Set(allTeams);

    setGameFilter((prev) => {
      const N = new Set();
      for (const k of prev) if (gameKeys.has(k)) N.add(k);
      return N;
    });
    setGameExcl((prev) => {
      const N = new Set();
      for (const k of prev) if (gameKeys.has(k)) N.add(k);
      return N;
    });
    setTeamExcl((prev) => {
      const N = new Set();
      for (const t of prev) if (teams.has(t)) N.add(t);
      return N;
    });
    setTeamInclude((prev) => {
      const N = new Set();
      for (const t of prev) if (teams.has(t)) N.add(t);
      return N;
    });
  }, [site, slate, games, allTeams]);
/* sorting + filter pipeline */
  const [sort, setSort] = useState({ col: "eproj", dir: "desc" });
  const toggleSort = (col) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }
    );

  const [useCounts, setUseCounts] = useState({});
  const usgPctByName = useMemo(() => {
    // total player uses across all lineups
    const totalUses = Object.values(useCounts || {}).reduce(
      (acc, c) => acc + (Number(c) || 0),
      0
    );

    // Each lineup uses exactly this many slots
    const slotsPerLineup = SITES[site].slots.length || 1;

    // Infer how many lineups were actually built
    const inferredLineups =
      slotsPerLineup > 0 ? totalUses / slotsPerLineup : 0;

    const denom = Math.max(1, inferredLineups || 0);

    const map = new Map();
    for (const [name, c] of Object.entries(useCounts || {})) {
      const uses = Number(c) || 0;
      map.set(name, (uses / denom) * 100);
    }
    return map;
  }, [useCounts, site]);

const view = useMemo(() => {
  // Position filter
const posOK = (r) => {
  if (posFilter === "ALL") return true;
  if (!r.posList || r.posList.length === 0) return true;

  const base = posFilter.replace(/[12]$/, ""); // handles PG1/PG2 style if ever used

  if (base === "G") {
    // Show both PG & SG
    return r.posList.includes("PG") || r.posList.includes("SG");
  }
  if (base === "F") {
    // Show both SF & PF
    return r.posList.includes("SF") || r.posList.includes("PF");
  }
  if (base === "UTIL") {
    // Everyone is UTIL-eligible
    return true;
  }

  if (base === "G/F") return r.posList.some((p) => ["PG", "SG", "SF", "PF"].includes(p));
  if (base === "F/C") return r.posList.some((p) => ["SF", "PF", "C"].includes(p));

  // Normal positions: PG / SG / SF / PF / C
  return r.posList.includes(base);
};

  // Text search filter
  const txt = q.trim().toLowerCase();
  const txtOK = (r) =>
    !txt ||
    r.name.toLowerCase().includes(txt) ||
    (r.team && r.team.toLowerCase().includes(txt)) ||
    (r.opp && r.opp.toLowerCase().includes(txt));

  // Game include/exclude sets
  const gameOK = (r) =>
    (gameFilter.size === 0 || gameFilter.has(r.gameKey)) &&
    !gameExcl.has(r.gameKey);

  // Team excludes
  const teamExOK = (r) => !teamExcl.has(r.team);

  // Team include-only
  const teamInOK = (r) =>
    teamInclude.size === 0 || teamInclude.has(r.team);

  // Slate filter: "both" shows in *both* tabs
  const slateOK = (r) => {
    if (slate === "late") return r.slate === "late" || r.slate === "both";
    return r.slate === "main" || r.slate === "both";
  };

  // 🔹 NEW: salary filter — hide players over 20k
  const salaryOK = (r) => !r.salary || r.salary <= SALARY_RENDER_MAX;

  // Apply filters before sorting
  const arr = rows.filter(
    (r) =>
      salaryOK(r) &&          // 👈 added here
      (!hideUnprojected || !r.siteIdOnly) &&
      (!hideExcluded || !effectiveExcls.has(r.name)) &&
      slateOK(r) &&
      posOK(r) &&
      txtOK(r) &&
      gameOK(r) &&
      teamExOK(r) &&
      teamInOK(r)
  );

  // Sorting (unchanged)
if (sort) {
  const dir = sort.dir === "asc" ? 1 : -1;
  const col = sort.col;

  arr.sort((a, b) => {
    let av;
    let bv;

    if (col === "usg") {
      // Use computed USG% (same as in the column)
      av = usgPctByName.get(a.name) || 0;
      bv = usgPctByName.get(b.name) || 0;
    } else if (col === "eproj") {
      // Keep row order stable when custom projections / boosts change.
      // Sort by the original base projection while displaying the edited/boosted number.
      av = a.baseProj || 0;
      bv = b.baseProj || 0;
    } else if (col === "val") {
      // Same idea as projection: sort by BASE value to avoid reordering
      // when boosts change the displayed value.
      av = a.baseVal || 0;
      bv = b.baseVal || 0;
    } else {
      av = a[col];
      bv = b[col];
    }

    if (typeof av === "string") av = av.toLowerCase();
    if (typeof bv === "string") bv = bv.toLowerCase();

    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

  return arr;
}, [
  rows,
  posFilter,
  q,
  gameFilter,
  teamExcl,
  teamInclude,
  gameExcl,
  sort,
  usgPctByName,
  slate,
  hideUnprojected,
  hideExcluded,
  effectiveExcls,
]);

  /* results + team counts */
  const [lineups, setLineups] = useStickyState(key("lineups"), []); // [{players:[], assignment?, total, salary}]
  const [lineupIds, setLineupIds] = useStickyState(key("lineupIds"), []);
  const [teamCounts, setTeamCounts] = useState({});
  const [cardsSortBy, setCardsSortBy] = useStickyState(key("cardsSortBy"), "proj");
  const [cardsSortDir, setCardsSortDir] = useStickyState(key("cardsSortDir"), "desc");
  const [stopInfo, setStopInfo] = useState(null);
  const [progress, setProgress] = useState(0);
  const [running, setRunning] = useState(false);

  function bumpUsage(players) {
    setUseCounts((prev) => {
      const next = { ...prev };
      for (const nm of players) next[nm] = (next[nm] || 0) + 1;
      return next;
    });
  }
  function bumpTeams(players) {
    const byName = new Map(rows.map((r) => [r.name, r.team]));
    setTeamCounts((prev) => {
      const next = { ...prev };
      for (const nm of players) {
        const t = byName.get(nm);
        if (!t) continue;
        next[t] = (next[t] || 0) + 1;
      }
      return next;
    });
  }
  function resetResults() {
    setLineups([]);
    setLineupIds([]);
    setStopInfo(null);
    setProgress(0);
    setUseCounts({});
    setTeamCounts({});
  }

  async function runSolve(payload, onItem, onDone) {
    let emitted = 0;
    try {
      await solveStreamNBA(payload, (L) => {
        emitted++;
        onItem(L);
      }, onDone);
    } catch (e) {}
    if (emitted === 0) {
      await solveFallbackNBA(payload, onItem);
      onDone?.({ done: true });
    }
  }

    async function optimize() {
    if (!rows.length) return;
    resetResults();
    setRunning(true);

    const currentLineups = [];
    const currentIds = [];

    const eligible = rows.filter((r) => {
      const slateOK = (rr) => {
        if (slate === "late") return rr.slate === "late" || rr.slate === "both";
        return rr.slate === "main" || rr.slate === "both";
      };
      if (!slateOK(r)) return false;

      const sid = getSiteId(r.name).id;
      if (!sid) return false;

      if (r.salary && r.salary > SALARY_RENDER_MAX) return false;
      if (effectiveExcls.has(r.name)) return false;
      if (r.gameKey && gameExcl.has(r.gameKey)) return false;
      if (r.team && teamExcl.has(r.team)) return false;
      if (teamInclude.size > 0 && r.team && !teamInclude.has(r.team)) return false;
      if (gameFilter.size > 0 && r.gameKey && !gameFilter.has(r.gameKey)) return false;

      return true;
    });

    const payload = {
      site,
      slots: SITES[site].slots,
      cap: Math.min(SITES[site].cap, Number(maxSalary) || SITES[site].cap),
      objective: "proj",
      players: eligible.map((r) => ({
        name: r.name,
        pos: r.posDisp,
        team: r.team,
        opp: r.opp,
        salary: Math.round(r.salary || 0),
        proj: r.eproj || 0,
        pown: r.pown || 0,
      })),
      locks: Array.from(locks),
      excludes: Array.from(effectiveExcls),
      boosts: boost,
      randomness: clamp(Number(randomness) || 0, 0, 100),
      global_max_pct: clamp(Number(globalMax) || 100, 0, 100),
      min_pct: Object.fromEntries(
        Object.entries(minPct).map(([k, v]) => [k, clamp(Number(v) || 0, 0, 100)])
      ),
      max_pct: Object.fromEntries(
        Object.entries(maxPct).map(([k, v]) => [k, clamp(Number(v) || 100, 0, 100)])
      ),
      max_from_team:
        String(maxFromTeam).trim() === ""
          ? null
          : Math.max(1, Number(maxFromTeam) || 1),
      lineup_pown_max:
        String(maxLineupPown).trim() === "" ? null : Number(maxLineupPown),
      geo_mean_pown_max:
        String(maxGeoMeanPown).trim() === "" ? null : Number(maxGeoMeanPown),
      groups,
      n: Math.max(1, Number(numLineups) || 1),
      min_diff: 1,
      time_limit_ms: 3000,
    };

    try {
      let seq = 1;

      await runSolve(
        payload,
        (evt) => {
          const names = Array.isArray(evt.players) ? evt.players : [];
          const lineup = {
            players: names,
            assignment: Array.isArray(evt.assignment)
              ? evt.assignment.map((x) => ({ slot: x.slot, name: x.name }))
              : null,
            total: evt.total || 0,
            salary: evt.salary || 0,
          };

          const usageNames = lineup.assignment
            ? lineup.assignment.map((x) => x.name)
            : lineup.players;

          bumpUsage(usageNames);
          bumpTeams(usageNames);

          currentLineups.push(lineup);
          const lid = `nba_${site}_${String(seq).padStart(4, "0")}`;
          currentIds.push(lid);

          setLineups((p) => [...p, lineup]);
          setLineupIds((p) => [...p, lid]);

          seq += 1;
          setProgress((x) => Math.min(Number(numLineups) || 1, x + 1));
        },
        (done) => {
          if (done?.reason) setStopInfo(done);

          const finalLineups = [...currentLineups];
          const finalIds = [...currentIds];

          if (finalLineups.length) {
            const id = `build_${Date.now()}`;
            const build = {
              id,
              label: `Build ${builds.length + 1}`,
              createdAt: Date.now(),
              lineups: finalLineups,
              lineupIds: finalIds,
            };

            setBuilds((prev) => [...prev, build]);
            setActiveBuildId(id);
            setLineups(finalLineups);
            setLineupIds(finalIds);
            recomputeExposure(finalLineups);
          }
        }
      );
    } finally {
      setRunning(false);
    }
  }


  /* exposure + ui */
  const lineupCount = lineups.length || Math.max(1, Number(numLineups) || 1);
  const slotsPerLineup = SITES[site].slots.length;
  const totalSlots = lineups.length * slotsPerLineup;

  const posByName = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.name, r.posList));
    return m;
  }, [rows]);

  const [expoTab, setExpoTab] = useState("ALL");
  const [expoView, setExpoView] = useState("players");
  const th = "px-1.5 py-[3px] text-[11px] font-semibold text-center bg-gray-50 select-none";
  const td = "px-1.5 py-[3px] text-[11px] text-center leading-tight";
  const POS_LINK = ({ label, active, onClick }) => (
    <button
      onClick={onClick}
      className={
        "relative pb-2 text-xs font-semibold border-b-2 transition " +
        (active ? "border-sky-400 text-sky-500" : "border-transparent text-slate-500 hover:text-sky-500")
      }
      style={{ marginRight: 14 }}
    >
      {label}
    </button>
  );

  function fillSlotsForLineup(names, assignment) {
    // ============================================================
    // NBA late-swap slot ordering
    //
    // DK:
    // PG / SG / SF / PF / C = earliest start, then cheapest
    // G / F                  = next-most-flexible slots
    // UTIL                   = latest start, then most expensive
    //
    // FD:
    // PG / SG / SF / PF / C = earliest start, then cheapest
    // G/F / F/C              = next-most-flexible slots
    // UTIL1 / UTIL2          = latest start, then most expensive
    //
    // The selected players never change; only their roster slots do.
    // Backtracking keeps every assignment position-legal.
    // ============================================================

    const order = site === "dk" ? ORDER_DK : ORDER_FD;
    const slotDefs = SITES[site].slots;
    const rowByName = new Map(rows.map((r) => [r.name, r]));

    const rawNames =
      Array.isArray(names) && names.length
        ? names
        : Array.isArray(assignment)
        ? assignment.map((x) => x?.name)
        : [];

    const uniqueNames = [
      ...new Set(rawNames.filter((name) => name && name !== "—")),
    ];

    const playerRows = uniqueNames
      .map((name) => rowByName.get(name))
      .filter(Boolean);

    // Preserve the original server assignment if anything is incomplete
    // or if a valid reordered assignment cannot be found.
    const serverFallback = () => {
      if (!Array.isArray(assignment) || !assignment.length) {
        return order.map((slot) => ({ slot, name: "—" }));
      }

      const bySlot = new Map();
      for (const item of assignment) {
        if (item?.slot) bySlot.set(item.slot, item?.name || "—");
      }

      return order.map((slot) => ({
        slot,
        name: bySlot.get(slot) || "—",
      }));
    };

    if (playerRows.length !== slotDefs.length) {
      return serverFallback();
    }

    const earlyCheapSort = (a, b) => {
      const timeA = nbaTimeToMinutes(a.time);
      const timeB = nbaTimeToMinutes(b.time);
      const validA = timeA >= 0;
      const validB = timeB >= 0;

      if (validA && !validB) return -1;
      if (!validA && validB) return 1;
      if (validA && validB && timeA !== timeB) return timeA - timeB;

      return (Number(a.salary) || 0) - (Number(b.salary) || 0);
    };

    const lateExpensiveSort = (a, b) => {
      const timeA = nbaTimeToMinutes(a.time);
      const timeB = nbaTimeToMinutes(b.time);
      const validA = timeA >= 0;
      const validB = timeB >= 0;

      if (validA && !validB) return -1;
      if (!validA && validB) return 1;
      if (validA && validB && timeA !== timeB) return timeB - timeA;

      return (Number(b.salary) || 0) - (Number(a.salary) || 0);
    };

    const eligibleForSlot = (player, slot) =>
      Array.isArray(player?.posList) &&
      player.posList.some((pos) => slot.eligible.includes(pos));

    const slotPriority =
      site === "dk"
        ? [
            { name: "UTIL", mode: "late" },
            { name: "G", mode: "late" },
            { name: "F", mode: "late" },
            { name: "PG", mode: "early" },
            { name: "SG", mode: "early" },
            { name: "SF", mode: "early" },
            { name: "PF", mode: "early" },
            { name: "C", mode: "early" },
          ]
        : [
            { name: "UTIL1", mode: "late" },
            { name: "UTIL2", mode: "late" },
            { name: "G/F", mode: "late" },
            { name: "F/C", mode: "late" },
            { name: "PG", mode: "early" },
            { name: "SG", mode: "early" },
            { name: "SF", mode: "early" },
            { name: "PF", mode: "early" },
            { name: "C", mode: "early" },
          ];

    const slotByName = new Map(slotDefs.map((slot) => [slot.name, slot]));
    const used = new Set();
    const picked = new Map();

    function assignSlot(index) {
      if (index >= slotPriority.length) return true;

      const config = slotPriority[index];
      const slot = slotByName.get(config.name);
      if (!slot) return false;

      const candidates = playerRows
        .filter(
          (player) =>
            !used.has(player.name) && eligibleForSlot(player, slot)
        )
        .slice()
        .sort(config.mode === "late" ? lateExpensiveSort : earlyCheapSort);

      for (const player of candidates) {
        used.add(player.name);
        picked.set(config.name, player.name);

        if (assignSlot(index + 1)) return true;

        picked.delete(config.name);
        used.delete(player.name);
      }

      return false;
    }

    if (!assignSlot(0)) {
      return serverFallback();
    }

    return order.map((slot) => ({
      slot,
      name: picked.get(slot) || "—",
    }));
  }

  const lineupsForCards = useMemo(() => {
  const dir = cardsSortDir === "asc" ? 1 : -1;

  const getKey = (L) => {
    const filled = fillSlotsForLineup(L.players || [], L.assignment);

    const totProj = filled.reduce((s, x) => {
      const r = rows.find((rr) => rr.name === x.name);
      return s + (r?.eproj || 0);
    }, 0);

    const totPown = filled.reduce((s, x) => {
      const r = rows.find((rr) => rr.name === x.name);
      return s + (((r?.pown) || 0) * 100);
    }, 0);

    const geoMeanPown = geoMeanPct(
      filled
        .map((x) => rows.find((rr) => rr.name === x.name))
        .filter(Boolean),
      (r) => ((r?.pown) || 0) * 100
    );

    if (cardsSortBy === "salary") return Number(L.salary) || 0;
    if (cardsSortBy === "pown") return totPown || 0;
    if (cardsSortBy === "geo") return geoMeanPown || 0;

    return totProj || 0;
  };

  return [...(lineups || [])].sort((a, b) => {
    const ka = getKey(a);
    const kb = getKey(b);
    if (ka < kb) return -1 * dir;
    if (ka > kb) return 1 * dir;
    return 0;
  });
}, [lineups, cardsSortBy, cardsSortDir, rows, site]);

    // Recompute exposure / team counts from a given set of lineups
  function recomputeExposure(lineupsArr) {
    const use = {};
    const teams = {};
    const byNameTeam = new Map(rows.map((r) => [r.name, r.team]));

    for (const L of lineupsArr || []) {
      const names =
        Array.isArray(L.assignment) && L.assignment.length
          ? L.assignment.map((x) => x.name)
          : L.players || [];

      for (const nm of names) {
        if (!nm || nm === "—") continue;
        use[nm] = (use[nm] || 0) + 1;

        const t = byNameTeam.get(nm);
        if (t) teams[t] = (teams[t] || 0) + 1;
      }
    }

    setUseCounts(use);
    setTeamCounts(teams);
  }

  // Player exposure (used only)
  const playerExposure = useMemo(() => {
    const out = [];
    for (const [name, uses] of Object.entries(useCounts)) {
      if (!uses) continue;
      const row = rows.find((x) => x.name === name);
      if (!row) continue;
      const posList = posByName.get(name) || [];
      out.push({
        name,
        team: row.team,
        pos: posList.join("/") || row.posDisp,
        elig: posList,
        count: uses,
        pct: (uses / Math.max(1, lineupCount)) * 100,
      });
    }
    // add umbrella elig for tabs (G/F/UTIL)
    const expanded = out.map((p) => {
      const base = p.elig || [];
      const elig = new Set(base);
      if (base.includes("PG") || base.includes("SG")) elig.add("G");
      if (base.includes("SF") || base.includes("PF")) elig.add("F");
      if (base.some((p) => ["PG", "SG", "SF", "PF"].includes(p))) elig.add("G/F");
      if (base.some((p) => ["SF", "PF", "C"].includes(p))) elig.add("F/C");
      elig.add("UTIL");
      return { ...p, elig: Array.from(elig) };
    });
    return expanded.sort((a, b) => b.count - a.count);
  }, [useCounts, rows, lineupCount, posByName]);

  const filteredExposure = useMemo(() => {
    if (expoTab === "ALL") return playerExposure;
    const base = expoTab.replace(/[12]$/, "");
    return playerExposure.filter((p) => p.elig?.includes(base));
  }, [playerExposure, expoTab]);

  const teamExposure = useMemo(() => {
    const arr = Object.entries(teamCounts)
      .filter(([, count]) => count > 0)
      .map(([team, count]) => ({
        team,
        count,
        pct: totalSlots ? (count / totalSlots) * 100 : 0,
      }));
    return arr.sort((a, b) => b.count - a.count);
  }, [teamCounts, totalSlots]);

  // CSV export (DK/FD exact formats)
  function exportLineupsCSV() {
    if (!lineups.length) return;

    const header = site === "dk"
      ? ["PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"]
      : ["PG", "SG", "SF", "PF", "C", "G/F", "F/C", "UTIL", "UTIL"];

    const order = site === "dk" ? ORDER_DK : ORDER_FD;
    const rowsOut = [header];

    for (let i = 0; i < lineups.length; i++) {
      const L = lineups[i];
      const filled = fillSlotsForLineup(L.players || [], L.assignment);
      const row = order.map((slot) => {
        const name = filled.find((a) => a.slot === slot)?.name || "";
        if (!name || name === "—") return "";
        const { id, slate } = getSiteId(name);
        if (site === "dk") {
          // DK: Player Name (player_id)
          return `${name} (${id || ""})`;
        } else {
          // FD: slate-playerId:Player Name
          const left = [slate || "", id || ""].filter(Boolean).join("-");
          return `${left}:${name}`;
        }
      });
      rowsOut.push(row);
    }

    const csv = toCsv(rowsOut);
    const fname = `nba_${site}_lineups_${new Date().toISOString().slice(0, 10)}.csv`;
    download(fname, csv);
  }

  // CSV export (Detailed: Lineup #, Site, Salary, Total Proj, Total pOWN, slots with IDs)
  function exportLineupsDetailedCSV() {
    if (!lineups.length) return;

    const slotOrder = site === "dk" ? ORDER_DK : ORDER_FD;

    // Header row
    const header = [
      "Lineup #",
      "Site",
      "Salary",
      "Total Proj",
      "Total pOWN",
      ...slotOrder,
    ];
    const rowsOut = [header];

    // Map player name -> row (for proj + pOWN)
    const rowByName = new Map(rows.map((r) => [r.name, r]));

    for (let i = 0; i < lineups.length; i++) {
      const L = lineups[i];

      // Fill slots in proper order (PG, SG, ... / PG1, PG2, ...)
      const filled = fillSlotsForLineup(L.players || [], L.assignment);

      // slot -> raw name lookup
      const bySlot = {};
      for (const s of slotOrder) bySlot[s] = "";
      for (const a of filled) {
        if (!a || !a.slot) continue;
        if (slotOrder.includes(a.slot)) {
          const nm = a.name === "—" ? "" : a.name || "";
          bySlot[a.slot] = nm;
        }
      }

      // Total projection: prefer solver total, fall back to sum of eproj
      let totalProj = Number(L.total || 0);
      if (!totalProj) {
        totalProj = 0;
        for (const nm of L.players || []) {
          const r = rowByName.get(nm);
          if (r && typeof r.eproj === "number") {
            totalProj += r.eproj;
          }
        }
      }

      // Total pOWN% (sum of player percents)
      let totalPown = 0;
      for (const nm of L.players || []) {
        const r = rowByName.get(nm);
        if (r && typeof r.pown === "number") {
          totalPown += r.pown * 100; // pown stored as fraction
        }
      }

      const row = [
        i + 1,                             // Lineup #
        site.toUpperCase(),                // Site
        L.salary || 0,                     // Salary
        totalProj.toFixed(1),              // Total Proj
        totalPown.toFixed(1),              // Total pOWN
      ];

      // Append players by slot order, with IDs just like exportLineupsCSV
      for (const slot of slotOrder) {
        const nm = bySlot[slot] || "";
        if (!nm) {
          row.push("");
          continue;
        }

        const { id, slate } = getSiteId(nm);
        if (site === "dk") {
          // DK: Player Name (player_id)
          row.push(`${nm} (${id || ""})`);
        } else {
          // FD: slate-playerId:Player Name
          const left = [slate || "", id || ""].filter(Boolean).join("-");
          row.push(`${left}:${nm}`);
        }
      }

      rowsOut.push(row);
    }

    const csv = toCsv(rowsOut);
    const fname = `nba_${site}_lineups_detailed_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    download(fname, csv);
  }

  /* ------------------------------ RESET BUTTONS ------------------------------ */
  const resetBoosts = () => {
    setBoost({});
    setTeamBoost({});
    setCustomProj({});
    setCustomProjDraft({});
  };
  const resetLocks = () => setLocks(new Set());
  const resetExcludes = () => {
    setExcls(new Set());
    setAllowedUnprojected(new Set());
  };
  const resetMinMax = () => {
    setMinPct({});
    setMaxPct({});
  };
  const resetConstraints = () => {
    setGlobalMax(100);
    setRandomness(0);
    setMaxLineupPown("");
    setMaxFromTeam("");
    setMaxGeoMeanPown("");
  };
  const resetFilters = () => {
    setGameFilter(new Set());
    setGameExcl(new Set());
    setTeamExcl(new Set());
    setTeamInclude(new Set());
    setPosFilter("ALL");
    setQ("");
    setHideUnprojected(true);
    setHideExcluded(false);
  };

    const clearAllBuilds = () => {
    setBuilds([]);
    setActiveBuildId(null);
    resetResults(); // clears lineups, ids, exposure, progress, stopInfo
  };

  const resetAllRules = () => {
    resetBoosts();
    resetLocks();
    resetExcludes();
    resetMinMax();
    resetConstraints();
    resetFilters();
    resetResults();
    clearAllBuilds();
  };

  /* ------------------------------ render ------------------------------ */
  return (
    <div className={`nba-opt ${isDark ? "theme-dark" : "theme-light"} min-h-screen px-4 md:px-6 py-5`}>
<style>{`
  /* ---------------- NBA Optimizer theme wrapper ---------------- */
  .nba-opt.theme-dark{
    background:#020617;
    min-height:100vh;
    color:#f1f5f9;
  }
  .nba-opt.theme-light { background:#f1f5f9; color:#0f172a; min-height:100vh; }
  .nba-opt { color-scheme:light; }
  .nba-opt.theme-dark { color-scheme:dark; }
  .nba-opt button { transition: background-color .15s, border-color .15s, color .15s; }
  .nba-opt button:focus-visible, .nba-opt summary:focus-visible {
    outline:2px solid #38bdf8; outline-offset:3px;
  }
  .nba-opt button:disabled { cursor:default; opacity:.55; }
  .nba-opt input:not([type="checkbox"]), .nba-opt select { border-width:1px; }
  .nba-opt input:focus-visible, .nba-opt select:focus-visible {
    outline:2px solid #38bdf8; outline-offset:1px;
  }
  .nba-opt input[type="checkbox"] { accent-color:#0ea5e9; }
  .nba-opt table { font-variant-numeric:tabular-nums; }
  .nba-opt .nba-section-heading { font-size:14px; font-weight:600; }
  .nba-opt .nba-panel { border-radius:1rem; }
  .nba-opt .nba-scroll { scrollbar-width:thin; scrollbar-color:#64748b transparent; }
  /* Keep the entire header above scrolling rows, with opaque cell backgrounds. */
  .nba-opt .nba-results { position:relative; isolation:isolate; }
  .nba-opt .nba-results thead { position:sticky; top:0; z-index:20; }
  .nba-opt .nba-results thead th { position:static; }
  .nba-opt.theme-dark .nba-results thead,
  .nba-opt.theme-dark .nba-results thead tr,
  .nba-opt.theme-dark .nba-results thead th { background:#020617 !important; }
  .nba-opt.theme-light .nba-results thead,
  .nba-opt.theme-light .nba-results thead tr,
  .nba-opt.theme-light .nba-results thead th { background:#f8fafc !important; }
  .nba-opt .nba-pool tbody tr:not(.row-locked):hover { background:#e2e8f0; }
  .nba-opt.theme-dark .nba-pool tbody tr:not(.row-locked):hover { background:#1e293b; }
  .nba-opt.theme-dark h1,
  .nba-opt.theme-dark h2,
  .nba-opt.theme-dark h3,
  .nba-opt.theme-dark h4{
    color:#f8fafc;
  }

  /* Utility overrides (Tailwind class names) scoped to NBA optimizer */
  .nba-opt.theme-dark .bg-white{ background-color: rgba(15, 23, 42, 0.72) !important; }
  .nba-opt.theme-dark .bg-gray-50{ background-color: rgba(2, 6, 23, 0.55) !important; }
  .nba-opt.theme-dark .bg-gray-100{ background-color: rgba(2, 6, 23, 0.72) !important; }

  .nba-opt.theme-dark .text-gray-900{ color:#f8fafc !important; }
  .nba-opt.theme-dark .text-gray-800{ color:#f1f5f9 !important; }
  .nba-opt.theme-dark .text-gray-700{ color:#e2e8f0 !important; }
  .nba-opt.theme-dark .text-gray-600{ color:#cbd5e1 !important; }
  .nba-opt.theme-dark .text-gray-500{ color:#94a3b8 !important; }
  .nba-opt.theme-dark .text-gray-400{ color:#94a3b8 !important; }

  .nba-opt.theme-dark .border-gray-100{ border-color: rgba(148,163,184,0.18) !important; }
  .nba-opt.theme-dark .border-gray-200{ border-color: rgba(148,163,184,0.18) !important; }
  .nba-opt.theme-dark .border-gray-300{ border-color: rgba(148,163,184,0.24) !important; }

  .nba-opt.theme-dark .shadow,
  .nba-opt.theme-dark .shadow-sm,
  .nba-opt.theme-dark .shadow-md{
    box-shadow: 0 12px 30px rgba(0,0,0,0.35) !important;
  }


  /* Custom projection colors need !important because NBA theme input rules also use !important. */
  .nba-opt input.nba-proj-up{
    color:#22c55e !important;
    -webkit-text-fill-color:#22c55e !important;
    border-color:rgba(34,197,94,0.7) !important;
    background-color:rgba(34,197,94,0.14) !important;
    font-weight:700 !important;
  }
  .nba-opt.theme-dark input.nba-proj-up{
    color:#22c55e !important;
    -webkit-text-fill-color:#22c55e !important;
    border-color:rgba(34,197,94,0.7) !important;
    background-color:rgba(34,197,94,0.14) !important;
  }
  .nba-opt input.nba-proj-down{
    color:#ef4444 !important;
    -webkit-text-fill-color:#ef4444 !important;
    border-color:#ef4444 !important;
    background-color:rgba(239,68,68,0.14) !important;
    font-weight:700 !important;
  }
  .nba-opt.theme-dark input.nba-proj-down{
    color:#ef4444 !important;
    -webkit-text-fill-color:#ef4444 !important;
    border-color:rgba(239,68,68,0.7) !important;
    background-color:rgba(239,68,68,0.14) !important;
  }

  /* Inputs */
  .nba-opt.theme-dark input:not(.nba-proj-up):not(.nba-proj-down),
  .nba-opt.theme-dark select,
  .nba-opt.theme-dark textarea{
    background-color: rgba(2, 6, 23, 0.45) !important;
    border-color: rgba(148,163,184,0.22) !important;
    color:#e5e7eb !important;
  }
  .nba-opt.theme-dark input::placeholder,
  .nba-opt.theme-dark textarea::placeholder{
    color: rgba(148,163,184,0.70) !important;
  }

  /* Buttons that rely on hover:bg-gray-50 */
  .nba-opt.theme-dark .hover\:bg-gray-50:hover{ background-color: rgba(30, 41, 59, 0.55) !important; }
  .nba-opt.theme-dark .hover\:bg-gray-100:hover{ background-color: rgba(30, 41, 59, 0.70) !important; }

  /* Pills / tabs */
  .nba-opt.theme-dark .bg-blue-600{ background-color: #0ea5e9 !important; }
  .nba-opt.theme-dark .bg-blue-50{ background-color: rgba(14,165,233,0.14) !important; }
  .nba-opt.theme-dark .text-blue-600{ color: #93c5fd !important; }
  .nba-opt.theme-dark .bg-red-50{ background-color: rgba(239,68,68,0.10) !important; }

  /* Table header rows that are light by default */
  .nba-opt.theme-dark thead{ color: rgba(203,213,225,0.95) !important; }
  .nba-opt.theme-dark th{ border-color: rgba(148,163,184,0.18) !important; }
  .nba-opt.theme-dark tr{ border-color: rgba(148,163,184,0.18) !important; }

  /* Scroll containers / cards */
  .nba-opt.theme-dark .rounded-2xl,
  .nba-opt.theme-dark .rounded-xl{
    backdrop-filter: none;
  }
  .nba-opt.theme-dark thead,
  .nba-opt.theme-dark thead.sticky,
  .nba-opt.theme-dark thead tr,
  .nba-opt.theme-dark th{
    background-color:#020617 !important;
    color:#e5e7eb !important;
  }
  .nba-opt.theme-light thead,
  .nba-opt.theme-light thead.sticky,
  .nba-opt.theme-light thead tr,
  .nba-opt.theme-light th{
    background-color:#f8fafc !important;
  }
  .nba-opt.theme-dark tbody tr:nth-child(odd):not(.row-locked){
    background-color:rgba(15,23,42,0.72);
  }
  .nba-opt.theme-dark tbody tr:nth-child(even):not(.row-locked){
    background-color:rgba(2,6,23,0.78);
  }
  .nba-opt.theme-light tbody tr:nth-child(odd):not(.row-locked){
    background-color:#ffffff;
  }
  .nba-opt.theme-light tbody tr:nth-child(even):not(.row-locked){
    background-color:#f8fafc;
  }
  .nba-opt .row-locked{
    background-color:rgba(59,130,246,0.10) !important;
  }
`}
</style>
<div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 lg:flex-nowrap">
  <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
    <h1 className="whitespace-nowrap text-2xl md:text-3xl font-extrabold">NBA — Optimizer</h1>
    <div className={`flex flex-wrap items-baseline gap-x-3 text-xs ${mutedClass}`}>
      <span className="italic">Updated: {fmtUpdated(updatedAt)}</span>
      <span>{(rows || []).length.toLocaleString()} rows</span>
    </div>
  </div>
  <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">
  <div className={`inline-flex shrink-0 items-center rounded-full px-1 py-1 text-[11px] ring-1 ${isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300"}`}>
    <button type="button" onClick={() => setTheme("dark")} aria-pressed={isDark}
      className={`px-3 py-1 rounded-full font-medium transition ${isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>Dark</button>
    <button type="button" onClick={() => setTheme("light")} aria-pressed={!isDark}
      className={`px-3 py-1 rounded-full font-medium transition ${!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-200"}`}>Light</button>
  </div>  <div className="flex shrink-0 items-center gap-2">
    <span className={`text-xs ${mutedClass}`}>Click here if new slate →</span>
    <button type="button" className="h-8 px-4 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 active:bg-red-800" onClick={resetAllRules}>Reset</button>
  </div>  </div>
</div>

<section className={`nba-panel mb-3 p-3 ${panelClass}`} aria-label="Slate and site">
  <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:flex-nowrap">
      <div className={`inline-flex shrink-0 items-center gap-6 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
        <button type="button" onClick={() => setActiveTab("optimizer")} className={tabClass(activeTab === "optimizer")} aria-pressed={activeTab === "optimizer"}>
          Optimizer
          {activeTab === "optimizer" && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />}
        </button>
        <button type="button" onClick={() => setActiveTab("lateSwap")} className={tabClass(activeTab === "lateSwap")} aria-pressed={activeTab === "lateSwap"}>
          Late Swap
          {activeTab === "lateSwap" && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />}
        </button>
      </div>
      <div className={`inline-flex shrink-0 items-center gap-6 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
        {["dk", "fd"].map((s) => (
          <button key={s} type="button" onClick={() => setSite(s)} aria-pressed={site === s} className={tabClass(site === s)}>
            <img src={SITES[s].logo} alt="" className="w-4 h-4 object-contain" />
            <span>{SITES[s].label}</span>
            {site === s && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />}
          </button>
        ))}
      </div>
    <div className="flex flex-wrap items-center gap-3">
      <span className={`flex h-9 items-center pb-2 text-xs ${mutedClass}`}>Slate:</span>
      <div className={`inline-flex shrink-0 items-center gap-6 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
      {[{ key: "main", label: "Main" }, { key: "late", label: "Late" }].map((sl) => (
        <button key={sl.key} type="button" onClick={() => setSlate(sl.key)} aria-pressed={slate === sl.key}
          className={tabClass(slate === sl.key)}>
          {sl.label}
          {slate === sl.key && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />}
        </button>
      ))}
      </div>
    </div>
  </div>
</section>

<section className={`nba-panel mb-3 p-3 ${panelClass}`} aria-label="NBA build settings">
  <div className="nba-section-heading mb-3">Build Settings</div>
<div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3 items-end">
  <div>
    <label className={`block text-[11px] mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
      Lineups
    </label>
    <input
      className={`${inputClass} w-full`}
      value={numLineups}
      onChange={(e) => setNumLineups(e.target.value)}
    />
  </div>

  <div>
    <label className={`block text-[11px] mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
      Max salary
    </label>
    <input
      className={`${inputClass} w-full`}
      value={maxSalary}
      onChange={(e) => setMaxSalary(e.target.value)}
    />
  </div>

  <div>
    <label className={`block text-[11px] mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
      Global Max %
    </label>
    <input
      className={`${inputClass} w-full`}
      value={globalMax}
      onChange={(e) => setGlobalMax(e.target.value)}
    />
  </div>

  <div>
    <label className={`block text-[11px] mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
      Randomness %
    </label>
    <input
      className={`${inputClass} w-full`}
      value={randomness}
      onChange={(e) => setRandomness(e.target.value)}
    />
  </div>

  <div>
    <label className={`block text-[11px] mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
      Max lineup pOWN %
    </label>
    <input
      className={`${inputClass} w-full`}
      placeholder="0–600"
      value={maxLineupPown}
      onChange={(e) => setMaxLineupPown(e.target.value)}
    />
  </div>

  <div>
    <label className={`block text-[11px] mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
      Max Geo Mean pOWN%
    </label>
    <input
      className={`${inputClass} w-full`}
      placeholder="e.g. 25–35"
      value={maxGeoMeanPown}
      onChange={(e) => setMaxGeoMeanPown(e.target.value)}
    />
  </div>

  <div>
    <label className={`block text-[11px] mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
      Max from team
    </label>
    <input
      className={`${inputClass} w-full`}
      placeholder="—"
      value={maxFromTeam}
      onChange={(e) => setMaxFromTeam(e.target.value)}
    />
  </div>
</div>
</section>

{/* Game cards */}
<section className={`nba-panel mb-3 p-3 ${panelClass}`} aria-label="Games">
<div>
  <div className="flex items-center gap-2 mb-2">
    <div className="nba-section-heading mr-1">Games</div>

    <button
      type="button"
      className="h-7 px-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-xs"
      onClick={() =>
        setGameFilter(new Set(games.map((g) => [g.a, g.b].sort().join("@"))))
      }
    >
      Select all
    </button>

    <button
      type="button"
      className="h-7 px-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-xs"
      onClick={() => setGameFilter(new Set())}
    >
      Clear all
    </button>
  </div>

  <div className="overflow-x-auto">
    <div className="flex gap-2 min-w-max pb-1">
      {games.map((g) => {
        const key = [g.a, g.b].sort().join("@");
        const included = gameFilter.has(key);
        const excludedGame = gameExcl.has(key);
        const exclA = teamExcl.has(g.a);
        const exclB = teamExcl.has(g.b);

        const meta = gameMetaByKey.get(key) || { ou: 0, teams: {} };
        const ou = meta.ou || 0;
        const impA = meta.teams?.[g.a] || 0;
        const impB = meta.teams?.[g.b] || 0;

        return (
          <div
            key={key}
            role="button"
            tabIndex={0}
            onClick={() =>
              setGameFilter((prev) => {
                const N = new Set(prev);
                N.has(key) ? N.delete(key) : N.add(key);
                return N;
              })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setGameFilter((prev) => {
                  const N = new Set(prev);
                  N.has(key) ? N.delete(key) : N.add(key);
                  return N;
                });
              }
            }}
            className={
              "w-[168px] shrink-0 rounded-xl border overflow-hidden transition-all duration-150 cursor-pointer " +
              (excludedGame
                ? isDark
                  ? "border-red-700/50 bg-red-950/10"
                  : "border-red-300 bg-red-50"
                : included
                ? isDark
                  ? "border-sky-500/60 bg-slate-900/95"
                  : "border-sky-300 bg-white shadow-sm"
                : isDark
                ? "border-slate-800 bg-slate-900/90 hover:border-slate-700"
                : "border-slate-200 bg-white hover:border-slate-300")
            }
            title={`${g.a} @ ${g.b}${g.time ? ` • ${g.time}` : ""}`}
          >
            <div
              className={
                "px-2 py-1.5 border-b " +
                (isDark
                  ? "border-slate-800 bg-slate-800/40"
                  : "border-slate-200 bg-slate-50")
              }
            >
              <div className="grid grid-cols-[16px_minmax(0,1fr)_20px] items-center gap-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGameFilter((prev) => {
                      const N = new Set(prev);
                      N.has(key) ? N.delete(key) : N.add(key);
                      return N;
                    });
                  }}
                  className={
                    "w-4 h-4 rounded-[4px] border flex items-center justify-center text-[9px] leading-none " +
                    (included
                      ? isDark
                        ? "border-sky-400 bg-sky-500/15 text-sky-300"
                        : "border-sky-500 bg-sky-50 text-sky-700"
                      : isDark
                      ? "border-slate-700 text-slate-500"
                      : "border-slate-300 text-slate-400")
                  }
                  aria-label={included ? "Unselect game" : "Select game"}
                >
                  {included ? "✓" : ""}
                </button>

                <div
                  className={
                    "min-w-0 truncate text-center text-[11px] font-semibold whitespace-nowrap " +
                    (isDark ? "text-slate-300" : "text-slate-600")
                  }
                >
                  {g.time || "—"} {ou ? `• O/U ${ou.toFixed(1)}` : ""}
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGameExcl((prev) => {
                      const N = new Set(prev);
                      N.has(key) ? N.delete(key) : N.add(key);
                      return N;
                    });
                  }}
                  className={
                    "w-5 h-5 rounded-md border flex items-center justify-center text-[11px] leading-none " +
                    (excludedGame
                      ? "bg-red-600 text-white border-red-700"
                      : isDark
                      ? "border-slate-700 text-slate-400 hover:bg-slate-800"
                      : "border-slate-300 text-slate-500 hover:bg-slate-100")
                  }
                  title={excludedGame ? "Un-exclude game" : "Exclude game"}
                  aria-label={excludedGame ? "Unexclude game" : "Exclude game"}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="px-2 py-2">
              {[
                { abbr: g.a, excluded: exclA },
                { abbr: g.b, excluded: exclB },
              ].map((t, idx) => (
                <div
                  key={`${key}-${t.abbr}-${idx}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTeamExcl((prev) => {
                      const N = new Set(prev);
                      N.has(t.abbr) ? N.delete(t.abbr) : N.add(t.abbr);
                      return N;
                    });
                  }}
                  className={
                    "flex items-center justify-between rounded-lg px-2 py-1.5 " +
                    (idx === 0 ? "mb-1.5 " : "") +
                    (t.excluded
                      ? isDark
                        ? "bg-red-950/20 ring-1 ring-red-800/40"
                        : "bg-red-50 ring-1 ring-red-200"
                      : isDark
                      ? "bg-slate-800/45"
                      : "bg-slate-50")
                  }
                  title={t.excluded ? `Un-exclude ${t.abbr}` : `Exclude ${t.abbr}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={teamLogo(t.abbr)}
                      alt={t.abbr}
                      className="w-5 h-5 object-contain shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <span
                      className={
                        "font-semibold text-[11px] " +
                        (isDark ? "text-slate-100" : "text-slate-900")
                      }
                    >
                      {t.abbr}
                    </span>
                  </div>

                  <div className="ml-auto flex items-center gap-1.5 shrink-0">
                    <div className="flex flex-col items-center justify-center leading-none">
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); bumpTeamBoost(t.abbr, +1); }}
                        onKeyDown={(e) => e.stopPropagation()}
                        className={`h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition ${(teamBoost[t.abbr] || 0) > 0
                          ? isDark ? "text-emerald-300 bg-emerald-500/10" : "text-emerald-700 bg-emerald-50"
                          : isDark ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"}`}
                        title={`Boost ${t.abbr} projections +10%`} aria-label={`Boost ${t.abbr} projections +10%`}>
                        ▲
                      </button>
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); bumpTeamBoost(t.abbr, -1); }}
                        onKeyDown={(e) => e.stopPropagation()}
                        className={`h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition ${(teamBoost[t.abbr] || 0) < 0
                          ? isDark ? "text-rose-300 bg-rose-500/10" : "text-rose-700 bg-rose-50"
                          : isDark ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"}`}
                        title={`Reduce ${t.abbr} projections -10%`} aria-label={`Reduce ${t.abbr} projections -10%`}>
                        ▼
                      </button>
                    </div>
                    <div className="min-w-[30px] text-right leading-none">
                      <div className={`text-[11px] tabular-nums ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                        {t.abbr === g.a
                          ? (impA ? impA.toFixed(1) : (t.excluded ? "Excluded" : ""))
                          : (impB ? impB.toFixed(1) : (t.excluded ? "Excluded" : ""))}
                      </div>
                      {!!(teamBoost[t.abbr] || 0) && (
                        <div className={`text-[9px] tabular-nums mt-0.5 ${(teamBoost[t.abbr] || 0) > 0 ? "text-emerald-500" : "text-rose-500"}`}>
                          {(teamBoost[t.abbr] || 0) > 0 ? "+" : ""}{(teamBoost[t.abbr] || 0) * 10}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
</div>
</section>

<div className={`nba-panel mb-3 p-3 flex items-center gap-3 flex-wrap ${panelClass}`}>
  {!running ? (
    <button
      className="h-9 px-6 rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 text-sm font-semibold"
      onClick={optimize}
    >
      Optimize
    </button>
  ) : (
    <button
      className={
        "h-8 px-4 rounded-lg text-sm font-medium border " +
        (isDark
          ? "border-slate-700 bg-slate-900 text-slate-200"
          : "border-gray-300 bg-white text-gray-700")
      }
    >
      Running…
    </button>
  )}

  <div className={`flex-1 min-w-[100px] max-w-[360px] h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
    <div
      className="h-full bg-sky-500 transition-all"
      style={{
        width: `${Math.min(
          100,
          (progress / Math.max(1, Number(numLineups))) * 100
        )}%`,
      }}
    />
  </div>

  <div
    className={
      isDark
        ? "text-sm text-slate-300 min-w-[52px] font-medium"
        : "text-sm text-gray-700 min-w-[52px] font-medium"
    }
  >
    {progress}/{numLineups}
  </div>

  {stopInfo?.reason && (
    <div
      className={
        "text-[11px] px-2 py-1 rounded border " +
        (isDark
          ? "bg-yellow-900/20 text-yellow-200 border-yellow-700/50"
          : "bg-yellow-50 text-yellow-800 border-yellow-200")
      }
    >
      {stopInfo.detail || stopInfo.reason}
    </div>
  )}
      {/* Quick Reset Toolbar */}
<details className={`w-full border-t pt-3 ${isDark ? "border-slate-800" : "border-slate-200"}`}>
  <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
  <div className="mt-3 flex flex-wrap gap-2">
  <button
    className={
      "h-8 px-3 rounded-md border text-xs transition " +
      (isDark
        ? "bg-white hover:bg-gray-50 text-gray-900 border-gray-300"
        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-300")
    }
    onClick={resetBoosts}
  >
    Reset Boosts
  </button>

  <button
    className={
      "h-8 px-3 rounded-md border text-xs transition " +
      (isDark
        ? "bg-white hover:bg-gray-50 text-gray-900 border-gray-300"
        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-300")
    }
    onClick={resetLocks}
  >
    Reset Locks
  </button>

  <button
    className={
      "h-8 px-3 rounded-md border text-xs transition " +
      (isDark
        ? "bg-white hover:bg-gray-50 text-gray-900 border-gray-300"
        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-300")
    }
    onClick={resetExcludes}
  >
    Reset Excludes
  </button>

  <button
    className={
      "h-8 px-3 rounded-md border text-xs transition " +
      (isDark
        ? "bg-white hover:bg-gray-50 text-gray-900 border-gray-300"
        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-300")
    }
    onClick={resetMinMax}
  >
    Reset Min/Max
  </button>

  <button
    className={
      "h-8 px-3 rounded-md border text-xs transition " +
      (isDark
        ? "bg-white hover:bg-gray-50 text-gray-900 border-gray-300"
        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-300")
    }
    onClick={resetConstraints}
  >
    Reset Constraints
  </button>

  <button
    className={
      "h-8 px-3 rounded-md border text-xs transition " +
      (isDark
        ? "bg-white hover:bg-gray-50 text-gray-900 border-gray-300"
        : "bg-white hover:bg-gray-50 text-gray-900 border-gray-300")
    }
    onClick={resetFilters}
  >
    Reset Filters
  </button>
  </div>
</details>
</div>

<section className={`nba-panel nba-pool mb-4 overflow-hidden ${panelClass}`} aria-label="Player pool">
<div className={`p-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
    <h2 className="nba-section-heading">Player Pool</h2>
    <span className={`text-xs ${mutedClass}`}>{view.length.toLocaleString()} players shown</span>
  </div>
{/* POS ribbon + search */}
<div className="mb-3 flex flex-wrap items-center gap-y-3">
  {(site === "fd" ? ["ALL", "PG", "SG", "SF", "PF", "C", "G/F", "F/C", "UTIL"] : ["ALL", "PG", "SG", "SF", "PF", "C", "G", "F", "UTIL"]).map((p) => (
    <POS_LINK key={p} label={p} active={posFilter === p} onClick={() => setPosFilter(p)} />
  ))}
  <input
    className={`${inputClass} w-full sm:w-72 sm:ml-auto`}
    placeholder="Search player / team / opp / pos / salary"
    value={q}
    onChange={(e) => setQ(e.target.value)}
  />
</div>

{/* Player visibility controls only affect the table, not solver eligibility. */}
<div className="mb-1 flex flex-wrap items-center gap-3">
  <label className={`h-8 px-3 rounded-md border text-xs inline-flex items-center gap-2 cursor-pointer ${isDark ? "border-slate-700 text-slate-200" : "border-slate-300 bg-white text-slate-700"}`}>
    <input type="checkbox" checked={hideUnprojected} onChange={(e) => setHideUnprojected(e.target.checked)} />
    Hide Unprojected
  </label>
  <label className={`h-8 px-3 rounded-md border text-xs inline-flex items-center gap-2 cursor-pointer ${isDark ? "border-slate-700 text-slate-200" : "border-slate-300 bg-white text-slate-700"}`}>
    <input type="checkbox" checked={hideExcluded} onChange={(e) => setHideExcluded(e.target.checked)} />
    Hide Excluded Players
  </label>
</div>

</div>
{/* players table */}
<div className="overflow-hidden">
  {/* ↓ adjust this number to taste (in pixels) */}
  <div className="nba-scroll overflow-auto" style={{ maxHeight: 520 }}>
    <table className="w-full text-[11px] table-auto">
      <thead className="sticky top-0 bg-gray-50 z-10">
        <tr>
          <th className={th}>Lock</th>
          <th className={th}>Excl</th>
          <th className={th} onClick={() => toggleSort("pos")} style={{ cursor: "pointer" }}>
            Pos
          </th>
          <th className={th}>Boost</th>
          <th
            className={th}
            onClick={() => toggleSort("name")}
            style={{ cursor: "pointer" }}
          >
            Player
          </th>
          <th
            className={th}
            onClick={() => toggleSort("team")}
            style={{ cursor: "pointer" }}
          >
            Tm
          </th>
          <th className={th} onClick={() => toggleSort("opp")} style={{ cursor: "pointer" }}>
            Opp
          </th>
          <th
            className={th}
            onClick={() => toggleSort("salary")}
            style={{ cursor: "pointer" }}
          >
            Salary
          </th>
          <th className={th} onClick={() => toggleSort("time")} style={{ cursor: "pointer" }}>
            Time
          </th>
          <th
            className={th}
            onClick={() => toggleSort("eproj")}
            style={{ cursor: "pointer" }}
          >
            Proj
          </th>
          <th className={th} onClick={() => toggleSort("val")} style={{ cursor: "pointer" }}>
            Val
          </th>
          <th className={th} onClick={() => toggleSort("pown")} style={{ cursor: "pointer" }}>
            pOWN%
          </th>
          <th className={th} onClick={() => toggleSort("usg")} style={{ cursor: "pointer" }}>
            USG%
          </th>
          <th className={th}>Min%</th>
          <th className={th}>Max%</th>
        </tr>
      </thead>

      <tbody>
        {view.map((r) => {
          const locked = locks.has(r.name);
          const defaultExcluded = r.siteIdOnly && !allowedUnprojected.has(r.name);
          const excluded = excls.has(r.name) || defaultExcluded;
          const usg = usgPctByName.get(r.name) || 0;
          // Color the value the user sees, including player/team boosts and live edits.
          // Compare at display precision so rounding cannot color an unchanged value.
          const draft = customProjDraft?.[r.name];
          const displayedProj = draft != null && String(draft).trim() !== "" && Number.isFinite(Number(draft))
            ? Number(draft)
            : Number(r.eproj ?? r.proj ?? 0);
          const projectionDelta = Number(displayedProj.toFixed(1)) - Number(Number(r.baseProj || 0).toFixed(1));

          return (
            <tr
              key={r.key + ":" + r.name}
              className={
                "border-t " +
                (locked ? "row-locked " : "")
              }
            >
              <td className={td}>
                <input
                  type="checkbox"
                  aria-label="Lock"
                  className="cursor-pointer"
                  checked={locked}
                  onChange={() =>
                    setLocks((prev) => {
                      const N = new Set(prev);
                      N.has(r.name) ? N.delete(r.name) : N.add(r.name);
                      return N;
                    })
                  }
                />
              </td>

              <td className={td}>
                <input
                  type="checkbox"
                  aria-label="Exclude"
                  className="cursor-pointer"
                  checked={excluded}
                  onChange={() => {
                    if (r.siteIdOnly && defaultExcluded) {
                      setAllowedUnprojected((prev) => {
                        const N = new Set(prev);
                        N.add(r.name);
                        return N;
                      });
                      return;
                    }

                    if (r.siteIdOnly && allowedUnprojected.has(r.name) && !excls.has(r.name)) {
                      setAllowedUnprojected((prev) => {
                        const N = new Set(prev);
                        N.delete(r.name);
                        return N;
                      });
                      return;
                    }

                    setExcls((prev) => {
                      const N = new Set(prev);
                      N.has(r.name) ? N.delete(r.name) : N.add(r.name);
                      return N;
                    });
                  }}
                />
              </td>

              <td className={td}>{r.posDisp}</td>

              <td className={td}>
                <div className="inline-flex items-center gap-1">
                  <button
                    className={
                      "w-5 h-5 rounded-md border text-[11px] transition " +
                      (isDark
                        ? "bg-slate-900/70 border-slate-700 text-slate-200 hover:bg-slate-800"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50")
                    }
                    onClick={() =>
                      setBoost((b) => ({ ...b, [r.name]: clamp((b[r.name] || 0) - 1, -9, 9) }))
                    }
                  >
                    −
                  </button>

                  <input
                    className={
                      "h-5 px-1 text-center text-[11px] rounded-md border w-9 " +
                      (isDark
                        ? "bg-slate-900/70 border-slate-700 text-slate-100"
                        : "bg-white border-gray-200 text-gray-900")
                    }
                    value={boost[r.name] ?? 0}
                    onChange={(e) =>
                      setBoost((b) => ({
                        ...b,
                        [r.name]: clamp(Math.round(+e.target.value || 0), -9, 9),
                      }))
                    }
                  />

                  <button
                    className={
                      "w-5 h-5 rounded-md border text-[11px] transition " +
                      (isDark
                        ? "bg-slate-900/70 border-slate-700 text-slate-200 hover:bg-slate-800"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50")
                    }
                    onClick={() =>
                      setBoost((b) => ({ ...b, [r.name]: clamp((b[r.name] || 0) + 1, -9, 9) }))
                    }
                  >
                    +
                  </button>
                </div>
              </td>

              <td className={td}>
                <div className="flex items-center justify-center gap-2 min-w-0">
                  <span className="truncate max-w-[260px] text-center" title={r.name}>
                    {r.name}
                  </span>
                </div>
              </td>

              <td className={td}>
                <TeamBadge team={r.team} />
              </td>

              <td className={td}>
                <TeamBadge team={r.opp} />
              </td>

              <td className={td}>{r.salary ? r.salary.toLocaleString() : "0"}</td>
              <td className={td}>{r.time || ""}</td>
              <td className={td}>
                <div className="relative inline-flex items-center justify-center align-middle">
                  <input
                    type="number"
                    step="0.1"
                    inputMode="decimal"
                    className={
                      "h-6 px-1.5 text-center text-[11px] rounded-md border w-16 tabular-nums " +
                      (projectionDelta > 0.000001
                        ? "nba-proj-up"
                        : projectionDelta < -0.000001
                        ? "nba-proj-down"
                        : isDark
                        ? "bg-slate-900/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
                        : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400")
                    }
                    value={
                      customProjDraft?.[r.name] != null
                        ? customProjDraft[r.name]
                        : (r.eproj ?? r.proj ?? 0).toFixed(1)
                    }
                    onChange={(e) => {
                      const raw = e.target.value;

                      setCustomProjDraft((m) => ({
                        ...m,
                        [r.name]: raw,
                      }));

                      if (raw == null || String(raw).trim() === "") {
                        setCustomProj((m) => {
                          const next = { ...m };
                          delete next[r.name];
                          return next;
                        });
                        return;
                      }

                      const shownProj = Number(raw);
                      if (!Number.isFinite(shownProj)) return;

                      const boostSteps = typeof boost[r.name] === "number" ? boost[r.name] : 0;
                      const teamSteps = Number(teamBoost[r.team]) || 0;
                      const mult = 1 + boostSteps * 0.03 + teamSteps * 0.10;
                      const baseCustomProj = mult !== 0 ? shownProj / mult : shownProj;

                      setCustomProj((m) => ({
                        ...m,
                        [r.name]: String(baseCustomProj),
                      }));
                    }}
                    onFocus={(e) => {
                      setCustomProjDraft((m) => ({
                        ...m,
                        [r.name]: String(e.target.value || (r.eproj ?? r.proj ?? 0).toFixed(1)),
                      }));
                      e.target.select();
                    }}
                    onBlur={() => {
                      setCustomProjDraft((m) => {
                        const next = { ...m };
                        delete next[r.name];
                        return next;
                      });
                    }}
                    title={
                      r.hasCustomProj
                        ? `Custom projection. Original: ${(r.baseProj ?? 0).toFixed(1)} | Current: ${(r.eproj ?? r.proj ?? 0).toFixed(1)}`
                        : "Click/type to enter a custom projection"
                    }
                  />
                  {r.hasCustomProj && (
                    <button
                      type="button"
                      aria-label={`Reset projection for ${r.name}`}
                      className={
                        "absolute -top-1.5 -right-1.5 z-10 w-3.5 h-3.5 inline-flex items-center justify-center rounded-full border text-[11px] leading-none transition " +
                        (isDark
                          ? "bg-slate-900/70 border-slate-700 text-slate-200 hover:bg-slate-800"
                          : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50")
                      }
                      title="Reset projection"
                      onClick={() => {
                        setCustomProj((m) => {
                          const next = { ...m };
                          delete next[r.name];
                          return next;
                        });
                        setCustomProjDraft((m) => {
                          const next = { ...m };
                          delete next[r.name];
                          return next;
                        });
                      }}
                    >
                      ↺
                    </button>
                  )}
                </div>
              </td>
              <td className={td}>{(r.val ?? 0).toFixed(1)}</td>
              <td className={td}>{((r.pown || 0) * 100).toFixed(1)}%</td>
              <td className={td}>{usg.toFixed(1)}%</td>

              <td className={td}>
                <input
                  className="h-5 px-1 text-center text-[11px] rounded-md border border-gray-200 w-11"
                  value={minPct[r.name] ?? ""}
                  onChange={(e) => setMinPct((m) => ({ ...m, [r.name]: e.target.value }))}
                />
              </td>

              <td className={td}>
                <input
                  className="h-5 px-1 text-center text-[11px] rounded-md border border-gray-200 w-11"
                  value={maxPct[r.name] ?? ""}
                  onChange={(e) => setMaxPct((m) => ({ ...m, [r.name]: e.target.value }))}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
</div>
</section>

{/* Build manager */}
{builds.length > 0 && (
  <div className="my-3 flex flex-wrap items-center gap-2">
    <span className={isDark ? "text-sm text-slate-400" : "text-sm text-gray-600"}>
      Builds:
    </span>

    <div className="flex flex-wrap items-center gap-2">
      {builds.map((b, idx) => {
        const active = activeBuildId === b.id;

        return (
          <div
            key={b.id}
            className={
              "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm " +
              (active
                ? "bg-blue-600 border-blue-600 text-white"
                : isDark
                ? "bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50")
            }
          >
            <button
              type="button"
              onClick={() => {
                setActiveBuildId(b.id);
                setLineups(b.lineups || []);
                setLineupIds(b.lineupIds || []);
                recomputeExposure(b.lineups || []);
              }}
              className="underline underline-offset-2"
            >
              {b.label || `Build ${idx + 1}`}
            </button>

            <span className={active ? "text-white/80 text-xs" : "text-gray-500 text-xs"}>
              {b.lineups?.length ?? 0}
            </span>

            <button
              type="button"
              className={
                "ml-1 rounded-full w-4 h-4 flex items-center justify-center " +
                (active
                  ? "bg-white/20 hover:bg-white/30"
                  : "bg-black/10 hover:bg-black/20")
              }
              title="Delete build"
              onClick={() => {
                const nextBuilds = builds.filter((x) => x.id !== b.id);
                setBuilds(nextBuilds);

                if (activeBuildId === b.id) {
                  if (!nextBuilds.length) {
                    setActiveBuildId(null);
                    resetResults();
                  } else {
                    const nextActive = nextBuilds[nextBuilds.length - 1];
                    setActiveBuildId(nextActive.id);
                    setLineups(nextActive.lineups || []);
                    setLineupIds(nextActive.lineupIds || []);
                    recomputeExposure(nextActive.lineups || []);
                  }
                }
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>

    <div className="flex items-start gap-2 ml-auto flex-wrap">
      {activeBuildId && (
        <>
          <button
            type="button"
            className={
              "h-9 px-3 rounded-md border text-sm font-semibold " +
              (isDark
                ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50")
            }
            onClick={() => {
              const current = builds.find((x) => x.id === activeBuildId);
              const newName = prompt("Rename build", current?.label || "") || "";
              if (!newName.trim()) return;
              setBuilds((prev) =>
                prev.map((x) =>
                  x.id === activeBuildId ? { ...x, label: newName.trim() } : x
                )
              );
            }}
          >
            Rename
          </button>

          <button
            type="button"
            className={
              "h-9 px-3 rounded-md border text-sm font-semibold " +
              (isDark
                ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50")
            }
            onClick={() => {
              const nextBuilds = builds.filter((x) => x.id !== activeBuildId);
              setBuilds(nextBuilds);

              if (!nextBuilds.length) {
                setActiveBuildId(null);
                resetResults();
              } else {
                const nextActive = nextBuilds[nextBuilds.length - 1];
                setActiveBuildId(nextActive.id);
                setLineups(nextActive.lineups || []);
                setLineupIds(nextActive.lineupIds || []);
                recomputeExposure(nextActive.lineups || []);
              }
            }}
          >
            Delete
          </button>
        </>
      )}

      <button
        type="button"
        className={
          "h-9 px-3 rounded-md border text-sm font-semibold " +
          (isDark
            ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
            : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50")
        }
        onClick={clearAllBuilds}
      >
        Clear All Builds
      </button>
    </div>
  </div>
)}

{/* Lineups and exposure share one responsive results row. */}
<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 items-stretch">
    {/* Lineup results */}
    <section
      aria-label="Lineups"
      className={`min-w-0 rounded-2xl p-3 ${
        isDark
          ? "border border-slate-800 bg-slate-900/95"
          : "border border-slate-200 bg-white"
      }`}
    >
      <div className="flex flex-wrap gap-3 items-center justify-between mb-3">
        <h2 className={`text-base font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
          Lineups ({lineups.length})
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <button
            className={
              "px-3 py-1.5 rounded text-sm border " +
              (isDark
                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50")
            }
            onClick={exportLineupsCSV}
            disabled={!lineups.length}
          >
            Export CSV
          </button>

          <button
            className={
              "px-3 py-1.5 rounded text-sm border " +
              (isDark
                ? "border-slate-700 bg-slate-950/40 text-slate-100 hover:bg-slate-800"
                : "border-slate-300 bg-white text-slate-900 hover:bg-gray-50")
            }
            onClick={exportLineupsDetailedCSV}
            disabled={!lineups.length}
          >
            Detailed CSV
          </button>
        </div>
      </div>

      {!lineups.length && <p className={`mb-3 text-xs ${mutedClass}`}>Run Optimize to generate lineups.</p>}
      <div className="nba-scroll nba-results overflow-auto max-h-[440px] rounded-lg">
        <table
          className={`w-full min-w-[520px] text-[12px] border-separate ${
            isDark ? "text-slate-100" : "text-slate-900"
          }`}
          style={{ borderSpacing: 0 }}
        >
          <thead className={isDark ? "bg-slate-950" : "bg-gray-50"}>
            <tr>
              <th className={th}>#</th>
              <th className={th}>Salary</th>
              <th className={th}>Total pOWN%</th>
              <th className={th}>Total Proj</th>
              <th className={th + " text-left"}>Players</th>
            </tr>
          </thead>

          <tbody>
            {lineups.map((L, i) => {
              const filled = fillSlotsForLineup(L.players || [], L.assignment);

              const totProj = filled.reduce((s, x) => {
                const r = rows.find((rr) => rr.name === x.name);
                return s + (r?.eproj || 0);
              }, 0);

              const totPown = filled.reduce((s, x) => {
                const r = rows.find((rr) => rr.name === x.name);
                return s + (((r?.pown) || 0) * 100);
              }, 0);

              const order = site === "dk" ? ORDER_DK : ORDER_FD;
              const playersStr = order
                .map((slot) => filled.find((x) => x.slot === slot)?.name || "—")
                .join(" • ");

              return (
                <tr
                  key={i}
                  className={
                    isDark
                      ? "odd:bg-slate-950/70 even:bg-slate-900/70"
                      : "odd:bg-white even:bg-gray-50"
                  }
                >
                  <td className={td}>{i + 1}</td>
                  <td className={td}>{(L.salary || 0).toLocaleString()}</td>
                  <td className={td}>{totPown.toFixed(1)}</td>
                  <td className={td}>{totProj.toFixed(1)}</td>
                  <td className={td + " text-left leading-snug"}>
                    <span className="break-words">{playersStr}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>

  <section className={`nba-panel min-w-0 overflow-hidden ${panelClass}`} aria-label="Exposure">
    <div className={`p-3 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-base font-bold">Exposure</h2>
        <div className={`inline-flex rounded-md border p-0.5 ${isDark ? "border-slate-700 bg-slate-950/40" : "border-slate-300 bg-white"}`} aria-label="Exposure view">
          {[{ key: "players", label: "Players" }, { key: "teams", label: "Teams" }].map((tab) => (
            <button key={tab.key} type="button" aria-pressed={expoView === tab.key} onClick={() => setExpoView(tab.key)}
              className={`px-3 py-1 rounded text-xs font-semibold transition ${expoView === tab.key
                ? "bg-sky-500 text-slate-950"
                : isDark ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900"}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {expoView === "players" ? (
      <div className="flex items-center gap-2 text-xs flex-wrap">
        {["ALL", "PG", "SG", "SF", "PF", "C", "UTIL"].map((t) => (
          <button
            key={t}
            onClick={() => setExpoTab(t)}
            className={`${
              expoTab === t
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white border border-gray-300 hover:bg-gray-50"
            } px-2 py-1 rounded`}
          >
            {t}
          </button>
        ))}
      </div>
      ) : (
        <p className={`text-xs ${mutedClass}`}>Share of total roster spots by team</p>
      )}
    </div>
    {expoView === "players" ? (
    <div className="nba-scroll overflow-auto" style={{ maxHeight: 440 }}>
      <table className="min-w-[360px] w-full text-xs">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr>
            <th className={th + " text-left"}>Player</th>
            <th className={th}>Pos</th>
            <th className={th}>Tm</th>
            <th className={th}>Count</th>
            <th className={th}>Exposure %</th>
          </tr>
        </thead>
        <tbody>
          {filteredExposure.map(({ name, team, pos, count, pct }) => (
            <tr key={name} className="border-t">
              <td className={td + " text-left"}>{name}</td>
              <td className={td}>{pos}</td>
              <td className={td}>
                <TeamBadge team={team} isDark={isDark} />
              </td>
              <td className={td}>{count}</td>
              <td className={td}>{pct.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    ) : (
    <div className="nba-scroll overflow-auto" style={{ maxHeight: 440 }}>
      <table className="min-w-[300px] w-full text-xs">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr>
            <th className={th}>Team</th>
            <th className={th}>Count</th>
            <th className={th}>Exposure %</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(teamCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([team, count]) => (
              <tr key={team} className="border-t">
                <td className={td}>
                  <TeamBadge team={team} isDark={isDark} />
                </td>
                <td className={td}>{count}</td>
                <td className={td}>
                  {(totalSlots ? (count / totalSlots) * 100 : 0).toFixed(1)}%
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
    )}
    {!lineups.length && <p className={`p-3 text-xs ${mutedClass}`}>Exposure will appear after optimizing.</p>}
  </section>
</div>

{/* Cards */}
<div className="mt-4 mb-3 flex items-center justify-between gap-3 flex-wrap">
  <div className="text-sm font-semibold">Cards</div>

  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-xs text-gray-600">Sort lineups by:</span>

    <select
      className="h-8 px-2 rounded-md border border-gray-300 bg-white text-xs"
      value={cardsSortBy}
      onChange={(e) => setCardsSortBy(e.target.value)}
    >
      <option value="proj">Projection</option>
      <option value="salary">Salary</option>
      <option value="pown">pOWN%</option>
      <option value="geo">Geo Mean%</option>
    </select>

    <button
      type="button"
      onClick={() => setCardsSortDir((d) => (d === "asc" ? "desc" : "asc"))}
      className="h-8 px-3 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-xs"
    >
      {cardsSortDir === "asc" ? "Asc ▲" : "Desc ▼"}
    </button>
  </div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
  {lineupsForCards.map((L, i) => {
    const filled = fillSlotsForLineup(L.players || [], L.assignment);

    const totProj = filled.reduce((s, x) => {
      const r = rows.find((rr) => rr.name === x.name);
      return s + (r?.eproj || 0);
    }, 0);

    const totPown = filled.reduce((s, x) => {
      const r = rows.find((rr) => rr.name === x.name);
      return s + (((r?.pown) || 0) * 100);
    }, 0);

    const geoMeanPown = geoMeanPct(
      filled
        .map((x) => rows.find((rr) => rr.name === x.name))
        .filter(Boolean),
      (r) => ((r?.pown) || 0) * 100
    );

      return (
        <div
          key={i}
          className={`rounded-2xl border p-3 ${
            isDark
              ? "border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
              : "border-slate-200 bg-white shadow-sm"
          }`}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0">
              <div className={isDark ? "text-base font-semibold text-slate-100" : "text-base font-semibold text-slate-900"}>
                Lineup #{i + 1}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <div className={isDark ? "text-[11px] font-medium text-slate-400" : "text-[11px] font-medium text-gray-600"}>
                Geo Mean% {geoMeanPown.toFixed(1)}
              </div>

              <img
                src={SITES[site]?.logo}
                alt={SITES[site]?.label || site}
                title={SITES[site]?.label || site}
                className="w-5 h-5 object-contain shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>

          <table className="w-full text-xs">
            <thead className={isDark ? "text-slate-400" : "text-gray-600"}>
              <tr>
                <th className="text-left py-1">Slot</th>
                <th className="text-left">Player</th>
                <th className="text-right">Proj</th>
                <th className="text-right">pOWN%</th>
                <th className="text-right">Sal</th>
              </tr>
            </thead>

            <tbody>
              {filled.map((rj, j) => {
                const base = rows.find((x) => x.name === rj.name);

                return (
                  <tr
                    key={`${rj.slot.replace(/[12]$/, "")}:${rj.name}:${j}`}
                    className={`border-t ${
                      isDark
                        ? j % 2 === 0
                          ? "border-slate-800 bg-slate-900/70"
                          : "border-slate-800 bg-slate-950/60"
                        : j % 2 === 0
                        ? "border-slate-200 bg-white"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <td className="py-1">{rj.slot.replace(/[12]$/, "")}</td>

                    <td className="py-1">
                      <div className="flex items-center gap-2">
                        {base?.team && (
                          <img
                            src={teamLogo(base.team)}
                            alt={base.team}
                            title={base.team}
                            className="inline-block align-middle w-5 h-5 shrink-0 object-contain"
                            style={{ width: 20, height: 20 }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                        <span className={isDark ? "whitespace-nowrap text-slate-100" : "whitespace-nowrap text-slate-900"}>
                          {rj.name}
                        </span>
                      </div>
                    </td>

                    <td className="py-1 text-right">
                      {base?.eproj ? base.eproj.toFixed(1) : "0.0"}
                    </td>

                    <td className="py-1 text-right">
                      {base?.pown ? (base.pown * 100).toFixed(1) : "0.0"}
                    </td>

                    <td className="py-1 text-right">
                      {base?.salary ? base.salary.toLocaleString() : "0"}
                    </td>
                  </tr>
                );
              })}

              <tr className={`border-t font-semibold ${isDark ? "border-slate-800" : "border-slate-200"}`}>
                <td className="py-1">Totals</td>
                <td />
                <td className="py-1 text-right">{totProj.toFixed(1)}</td>
                <td className="py-1 text-right">{totPown.toFixed(1)}</td>
                <td className="py-1 text-right">{(L.salary || 0).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
  })}
</div>
</div>
);
}
